import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { DayPicker } from 'react-day-picker';
import 'react-day-picker/dist/style.css';
import { useAuth } from '../context/AuthContext';
import MobileModal from './MobileModal';
import { ALL_EQUIPMENT_CLASSES, isErgEquipment, isLaunchEquipment, isReservableClass, tracksMeters, OAR_TYPES } from '../constants/boatClasses';
import { getAuthHeaders } from '../utils/api';
import { getSeatCountForBoatClass } from '../utils/boatSeats';
import {
  RESERVATION_TIME_OPTIONS,
  DEFAULT_RESERVATION_START_TIME,
  DEFAULT_RESERVATION_END_TIME,
  findOverlappingReservations,
  formatReservationTimeLabel,
  getLocalISODate,
  isoDateToReservationDate,
  reservationsOnDate
} from '../utils/reservationTimes';
import SearchableMemberChecklist from './SearchableMemberChecklist';
import MassBoatReservation from './MassBoatReservation';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';

function parseUsDateKey(dateStr) {
  if (!dateStr || typeof dateStr !== 'string') return 0;
  const parts = dateStr.split('/');
  if (parts.length !== 3) return 0;
  const month = parseInt(parts[0], 10);
  const day = parseInt(parts[1], 10);
  const year = parseInt(parts[2], 10);
  if (!month || !day || !year) return 0;
  return new Date(year, month - 1, day).getTime();
}

function meterSessionKey(m) {
  if (m.boat_meters_id != null) return m.boat_meters_id;
  if (typeof m.id === 'string' && m.id.startsWith('m-')) {
    const n = parseInt(m.id.slice(2), 10);
    return Number.isNaN(n) ? m.id : n;
  }
  return m.id;
}

function reservationMeterGroupKey(log) {
  if (!log) return null;
  const resId = log.reservation_id || log.reservationId;
  if (resId) return `res:${resId}`;
  const notes = String(log.notes || log.purpose || '');
  const match = notes.match(/reservation #(\d+)/i);
  if (!match) return null;
  const boatId = log.boat_id || log.boatId || '';
  return `note:${boatId}:${match[1]}`;
}

function collapseReservationMeterLogs(logs) {
  if (!Array.isArray(logs) || logs.length === 0) return [];
  const chosen = new Map();
  const order = [];
  for (const log of logs) {
    const key = reservationMeterGroupKey(log);
    if (!key) {
      order.push({ type: 'single', log });
      continue;
    }
    if (!chosen.has(key)) {
      chosen.set(key, log);
      order.push({ type: 'group', key });
    } else {
      const current = chosen.get(key);
      const currentSkip = current.skip_boat_total === true || current.skipBoatTotal === true;
      const incomingSkip = log.skip_boat_total === true || log.skipBoatTotal === true;
      if (currentSkip && !incomingSkip) chosen.set(key, log);
    }
  }
  return order.map((item) => (item.type === 'single' ? item.log : chosen.get(item.key)));
}

/** Past reservations get a `session_meters` array when a boat log matches same boat + date. Orphan logs stay as rows. */
function mergeReservationAndMeterHistory(reservations, meterSessions) {
  const res = (reservations || []).map((r) => ({
    ...r,
    entry_type: r.entry_type || 'reservation',
    session_meters: []
  }));
  const meters = collapseReservationMeterLogs(
    (meterSessions || []).map((m) => ({ ...m, entry_type: 'meters_log' }))
  );
  const attachedKeys = new Set();

  for (const m of meters) {
    const pastForBoatDate = res.filter(
      (r) =>
        r.entry_type === 'reservation' &&
        r.is_past &&
        r.boat_id === m.boat_id &&
        r.reservation_date === m.reservation_date
    );
    if (pastForBoatDate.length >= 1) {
      const pick =
        pastForBoatDate.length === 1
          ? pastForBoatDate[0]
          : [...pastForBoatDate].sort((a, b) =>
              String(b.end_time || '').localeCompare(String(a.end_time || ''))
            )[0];
      pick.session_meters.push({ ...m });
      attachedKeys.add(meterSessionKey(m));
    }
  }

  const orphanMeters = meters.filter((m) => !attachedKeys.has(meterSessionKey(m)));
  const combined = [...res, ...orphanMeters];
  combined.sort((a, b) => {
    const tb = parseUsDateKey(b.reservation_date);
    const ta = parseUsDateKey(a.reservation_date);
    if (tb !== ta) return tb - ta;
    if (a.entry_type === 'reservation' && b.entry_type !== 'reservation') return -1;
    if (a.entry_type !== 'reservation' && b.entry_type === 'reservation') return 1;
    if (a.entry_type === 'reservation') {
      return String(b.start_time || '').localeCompare(String(a.start_time || ''));
    }
    return String(b.created_at || '').localeCompare(String(a.created_at || ''));
  });
  return combined;
}

export default function BoatManagement({ token }) {
  const { isCoach, isAdmin, isSuperAdmin, user } = useAuth();
  const [boats, setBoats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [selectedBoat, setSelectedBoat] = useState(null);
  const [showMetersLog, setShowMetersLog] = useState(false);
  const [showReservations, setShowReservations] = useState(false);
  const [showDamageReports, setShowDamageReports] = useState(false);
  const [metersLog, setMetersLog] = useState([]);
  const [reservations, setReservations] = useState([]);
  const [myReservations, setMyReservations] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [activeTab, setActiveTab] = useState('boats'); // 'boats' | 'oars' | 'reservations' | 'upcomingSessions' | 'massReserve' | 'history' | 'meterLogs'
  const [searchQuery, setSearchQuery] = useState('');
  const [showErgsOnly, setShowErgsOnly] = useState(false);
  const [showLaunchesOnly, setShowLaunchesOnly] = useState(false);
  const [showRetired, setShowRetired] = useState(false);
  const [retireNotes, setRetireNotes] = useState('');
  const [showRetireModal, setShowRetireModal] = useState(null); // boat to retire
  
  // Oars state
  const [oars, setOars] = useState([]);
  const [oarsLoading, setOarsLoading] = useState(true);
  const [selectedOar, setSelectedOar] = useState(null);
  const [showAddOarForm, setShowAddOarForm] = useState(false);
  const [showOarDamageReports, setShowOarDamageReports] = useState(false);
  const [showOarReservations, setShowOarReservations] = useState(false);
  const [oarDamageReports, setOarDamageReports] = useState([]);
  const [oarDamageReportCounts, setOarDamageReportCounts] = useState({});
  const [oarReservations, setOarReservations] = useState([]);
  const [showRetiredOars, setShowRetiredOars] = useState(false);
  const [showOarRetireModal, setShowOarRetireModal] = useState(null);
  const [oarRetireNotes, setOarRetireNotes] = useState('');
  
  // Oar form state
  const [oarName, setOarName] = useState('');
  const [oarType, setOarType] = useState('');
  const [oarLengthCm, setOarLengthCm] = useState('');
  const [oarInboardCm, setOarInboardCm] = useState('');
  const [oarBladeType, setOarBladeType] = useState('');
  const [oarOwnershipType, setOarOwnershipType] = useState('club');
  const [oarOwnerIds, setOarOwnerIds] = useState([]);
  const [oarNotes, setOarNotes] = useState('');
  const [editingOar, setEditingOar] = useState(null);
  
  // Oar damage report form
  const [oarDamageSeverity, setOarDamageSeverity] = useState('minor');
  const [oarDamageDescription, setOarDamageDescription] = useState('');
  const [oarDamageLocation, setOarDamageLocation] = useState('');
  const [oarDamageDate, setOarDamageDate] = useState(new Date().toLocaleDateString('en-US'));
  const [showAddOarDamageReport, setShowAddOarDamageReport] = useState(false);
  const [oarDamageStatusFilter, setOarDamageStatusFilter] = useState('open');
  const [resolvingOarReport, setResolvingOarReport] = useState(null);
  const [oarResolutionNotes, setOarResolutionNotes] = useState('');
  
  // Session history state
  const [sessionHistory, setSessionHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyDateRange, setHistoryDateRange] = useState({ startDate: '', endDate: '' });
  const [myMetersLogsWeb, setMyMetersLogsWeb] = useState([]);
  const [myMetersLogsLoading, setMyMetersLogsLoading] = useState(false);

  // Club-wide upcoming reservations (calendar tab)
  const [clubCalendarReservations, setClubCalendarReservations] = useState([]);
  const [calendarLoading, setCalendarLoading] = useState(false);
  const [calendarMonth, setCalendarMonth] = useState(() => new Date());
  const [selectedCalendarDay, setSelectedCalendarDay] = useState(() => {
    const t = new Date();
    t.setHours(0, 0, 0, 0);
    return t;
  });

  // Damage reports state
  const [damageReports, setDamageReports] = useState([]);
  const [damageReportCounts, setDamageReportCounts] = useState({});
  const [showAddDamageReport, setShowAddDamageReport] = useState(false);
  const [damageSeverity, setDamageSeverity] = useState('minor');
  const [damageDescription, setDamageDescription] = useState('');
  const [damageLocation, setDamageLocation] = useState('');
  const [damageDate, setDamageDate] = useState(new Date().toLocaleDateString('en-US'));
  const [damageAssigneeUserId, setDamageAssigneeUserId] = useState('');
  const [damageStatusFilter, setDamageStatusFilter] = useState('open');
  const [resolvingReport, setResolvingReport] = useState(null);
  const [resolutionNotes, setResolutionNotes] = useState('');

  // Meter reminders state
  const [meterReminders, setMeterReminders] = useState([]);
  const [showDismissConfirm, setShowDismissConfirm] = useState(null);

  // Duplicate detection state
  const [showDuplicatePrompt, setShowDuplicatePrompt] = useState(false);
  const [existingLogs, setExistingLogs] = useState([]);
  const [pendingMeterData, setPendingMeterData] = useState(null);

  // Personal meters (assignee) overlap: separate from boat duplicate
  const [showAssigneeOverlapPrompt, setShowAssigneeOverlapPrompt] = useState(false);
  const [assigneeOverlapList, setAssigneeOverlapList] = useState([]);
  const [pendingOverlapSubmit, setPendingOverlapSubmit] = useState(null);

  // Form states
  const [boatName, setBoatName] = useState('');
  const [boatClass, setBoatClass] = useState('');
  const [weightClass, setWeightClass] = useState('');
  const [boatSpanCm, setBoatSpanCm] = useState('');
  const [ownershipType, setOwnershipType] = useState('club');
  const [ownerIds, setOwnerIds] = useState([]); // Support multiple owners
  const [meters, setMeters] = useState('');
  const [metersDate, setMetersDate] = useState(new Date().toLocaleDateString('en-US'));
  const [metersNotes, setMetersNotes] = useState('');
  const [metersAssignedUserIds, setMetersAssignedUserIds] = useState([]);
  const [showUserSelector, setShowUserSelector] = useState(false);
  const [metersSuccess, setMetersSuccess] = useState('');
  
  // Edit boat state
  const [editingBoat, setEditingBoat] = useState(null);
  const [editingBoatRigging, setEditingBoatRigging] = useState(null);
  const [showBoatRiggingForm, setShowBoatRiggingForm] = useState(false);

  // Oar rigging edit state
  const [editingOarRigging, setEditingOarRigging] = useState(null);
  const [showOarRiggingForm, setShowOarRiggingForm] = useState(false);
  
  // Edit meters log state
  const [editingLog, setEditingLog] = useState(null);
  const [editMeters, setEditMeters] = useState('');
  const [editDate, setEditDate] = useState('');
  const [editNotes, setEditNotes] = useState('');
  const [editAssignedUserId, setEditAssignedUserId] = useState('');
  
  // Reservation form states
  const [reservationDate, setReservationDate] = useState('');
  const [startTime, setStartTime] = useState(DEFAULT_RESERVATION_START_TIME);
  const [endTime, setEndTime] = useState(DEFAULT_RESERVATION_END_TIME);
  const [purpose, setPurpose] = useState('');
  const [justReservedId, setJustReservedId] = useState(null);
  const [submittingReservation, setSubmittingReservation] = useState(false);
  // State updates land after a re-render, so rapid taps would each pass a state check.
  const reservationInFlightRef = useRef(false);
  /** User IDs to auto-credit on meter log (same as reservation) */
  const [reservationAssignedUserIds, setReservationAssignedUserIds] = useState([]);
  const [editingCrewForReservation, setEditingCrewForReservation] = useState(null);
  const [editingCrewUserIds, setEditingCrewUserIds] = useState([]);
  /** When logging from a meter reminder, tie log to that reservation (disambiguation + pre-fill) */
  const [meterLogReservationId, setMeterLogReservationId] = useState(null);
  
  const [error, setError] = useState('');

  useEffect(() => {
    fetchBoats();
    fetchOars();
    fetchMyReservations();
    fetchDamageReportCounts();
    fetchOarDamageReportCounts();
    fetchMeterReminders();
    fetchTeamMembers();
  }, []);

  const fetchBoats = async (includeRetiredParam = showRetired) => {
    try {
      const url = (isCoach() || isAdmin()) && includeRetiredParam
        ? `${API_URL}/api/boats?includeRetired=all`
        : `${API_URL}/api/boats`;
      const response = await fetch(url, {
        headers: getAuthHeaders(token)
      });
      const data = await response.json();
      setBoats(data.boats || []);
      setLoading(false);
    } catch (err) {
      console.error('Failed to fetch boats:', err);
      setError('Failed to load boats');
      setLoading(false);
    }
  };

  const fetchOars = async (includeRetiredParam = showRetiredOars) => {
    try {
      const url = (isCoach() || isAdmin()) && includeRetiredParam
        ? `${API_URL}/api/oars?includeRetired=all`
        : `${API_URL}/api/oars`;
      const response = await fetch(url, {
        headers: getAuthHeaders(token)
      });
      const data = await response.json();
      setOars(data.oars || []);
      setOarsLoading(false);
    } catch (err) {
      console.error('Failed to fetch oars:', err);
      setOarsLoading(false);
    }
  };

  const fetchOarDamageReportCounts = async () => {
    try {
      const response = await fetch(`${API_URL}/api/oars/damage-reports/counts`, {
        headers: getAuthHeaders(token)
      });
      const data = await response.json();
      setOarDamageReportCounts(data.counts || {});
    } catch (err) {
      console.error('Failed to fetch oar damage report counts:', err);
    }
  };

  const fetchTeamMembers = async () => {
    try {
      const response = await fetch(`${API_URL}/api/boats/team-members`, {
        headers: getAuthHeaders(token)
      });
      const data = await response.json();
      setTeamMembers(data.members || []);
    } catch (err) {
      console.error('Failed to fetch team members:', err);
    }
  };

  const fetchMyReservations = async () => {
    try {
      const response = await fetch(`${API_URL}/api/boats/reservations/mine?upcoming=true`, {
        headers: getAuthHeaders(token)
      });
      const data = await response.json();
      setMyReservations(data.reservations || []);
    } catch (err) {
      console.error('Failed to fetch my reservations:', err);
    }
  };

  const formatDateMMDDYYYYApi = (d) => {
    const pad = (n) => String(n).padStart(2, '0');
    return `${pad(d.getMonth() + 1)}/${pad(d.getDate())}/${d.getFullYear()}`;
  };

  const calendarMonthBounds = (d) => {
    const start = new Date(d.getFullYear(), d.getMonth(), 1);
    const end = new Date(d.getFullYear(), d.getMonth() + 1, 0);
    return { start, end };
  };

  const fetchClubCalendarReservations = useCallback(
    async (monthDate) => {
      setCalendarLoading(true);
      try {
        const { start, end } = calendarMonthBounds(monthDate);
        const startStr = formatDateMMDDYYYYApi(start);
        const endStr = formatDateMMDDYYYYApi(end);
        const url = `${API_URL}/api/boats/reservations?startDate=${encodeURIComponent(
          startStr
        )}&endDate=${encodeURIComponent(endStr)}&upcoming=true`;
        const response = await fetch(url, {
          headers: getAuthHeaders(token)
        });
        const data = await response.json();
        if (response.ok) {
          setClubCalendarReservations(data.reservations || []);
        }
      } catch (err) {
        console.error('Failed to fetch club calendar reservations:', err);
      } finally {
        setCalendarLoading(false);
      }
    },
    [token]
  );

  const handleCalendarMonthChange = (m) => {
    setCalendarMonth(m);
    const { start, end } = calendarMonthBounds(m);
    const sel = selectedCalendarDay;
    if (sel < start || sel > end) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      if (today >= start && today <= end) {
        setSelectedCalendarDay(today);
      } else {
        setSelectedCalendarDay(new Date(start));
      }
    }
  };

  useEffect(() => {
    if (activeTab !== 'upcomingSessions') return;
    fetchClubCalendarReservations(calendarMonth);
  }, [activeTab, calendarMonth, fetchClubCalendarReservations]);

  // Damage report functions
  const fetchDamageReportCounts = async () => {
    try {
      const response = await fetch(`${API_URL}/api/boats/damage-reports/counts`, {
        headers: getAuthHeaders(token)
      });
      const data = await response.json();
      setDamageReportCounts(data.counts || {});
    } catch (err) {
      console.error('Failed to fetch damage report counts:', err);
    }
  };

  const fetchMeterReminders = async () => {
    try {
      const response = await fetch(`${API_URL}/api/boats/meter-reminders`, {
        headers: getAuthHeaders(token)
      });
      if (response.ok) {
        const data = await response.json();
        setMeterReminders(data.reminders || []);
      }
    } catch (err) {
      console.error('Failed to fetch meter reminders:', err);
    }
  };

  // Fetch user's session history
  const fetchSessionHistory = async (startDate = '', endDate = '') => {
    setHistoryLoading(true);
    try {
      let url = `${API_URL}/api/boats/reservations/history`;
      const params = new URLSearchParams();
      if (startDate) params.append('startDate', startDate);
      if (endDate) params.append('endDate', endDate);
      if (params.toString()) url += `?${params.toString()}`;

      const response = await fetch(url, {
        headers: getAuthHeaders(token)
      });
      if (response.ok) {
        const data = await response.json();
        setSessionHistory(
          mergeReservationAndMeterHistory(data.reservations || [], data.meter_sessions || [])
        );
      }
    } catch (err) {
      console.error('Failed to fetch session history:', err);
    } finally {
      setHistoryLoading(false);
    }
  };

  const fetchMyMetersLogs = async () => {
    setMyMetersLogsLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/boats/meters/my`, {
        headers: getAuthHeaders(token)
      });
      if (response.ok) {
        const data = await response.json();
        setMyMetersLogsWeb(collapseReservationMeterLogs(data.logs || []));
      }
    } catch (err) {
      console.error('Failed to fetch my meters logs:', err);
    } finally {
      setMyMetersLogsLoading(false);
    }
  };

  const dismissMeterReminder = async (reservationId) => {
    try {
      const response = await fetch(`${API_URL}/api/boats/meter-reminders/${reservationId}/dismiss`, {
        method: 'POST',
        headers: getAuthHeaders(token)
      });
      if (response.ok) {
        setMeterReminders(prev => prev.filter(r => r.id !== reservationId));
        setShowDismissConfirm(null);
      }
    } catch (err) {
      console.error('Failed to dismiss reminder:', err);
    }
  };

  const openMeterLogForReminder = (reminder) => {
    // Find the boat and open the meters log modal
    const boat = boats.find(b => b.id === reminder.boat_id);
    if (boat) {
      setSelectedBoat(boat);
      // Pre-fill the date from the reservation
      setMetersDate(reminder.reservation_date);
      setMeterLogReservationId(reminder.id);
      const fromEnriched = reminder.assignedUsers?.map((u) => String(u.id)) || [];
      const fromRaw = Array.isArray(reminder.assigned_user_ids)
        ? reminder.assigned_user_ids.map(String)
        : [];
      setMetersAssignedUserIds(fromEnriched.length > 0 ? fromEnriched : fromRaw);
      setShowMetersLog(true);
    }
  };

  const fetchBoatDamageReports = async (boatId, status = 'all') => {
    try {
      const response = await fetch(`${API_URL}/api/boats/${boatId}/damage-reports?status=${status}`, {
        headers: getAuthHeaders(token)
      });
      const data = await response.json();
      setDamageReports(data.reports || []);
    } catch (err) {
      console.error('Failed to fetch damage reports:', err);
      setError('Failed to load damage reports');
    }
  };

  const handleViewDamageReports = (boat) => {
    setSelectedBoat(boat);
    setShowDamageReports(true);
    setShowMetersLog(false);
    setShowReservations(false);
    fetchBoatDamageReports(boat.id, damageStatusFilter);
  };

  const handleSubmitDamageReport = async (e) => {
    e.preventDefault();
    setError('');

    if (!damageDescription.trim()) {
      setError('Please describe the damage');
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/boats/damage-reports`, {
        method: 'POST',
        headers: getAuthHeaders(token),
        body: JSON.stringify({
          boatId: selectedBoat.id,
          severity: damageSeverity,
          description: damageDescription,
          locationOnBoat: damageLocation,
          discoveredDate: damageDate,
          assigneeUserId: damageAssigneeUserId ? parseInt(damageAssigneeUserId, 10) : null
        })
      });

      const data = await response.json();

      if (response.ok) {
        setShowAddDamageReport(false);
        setDamageSeverity('minor');
        setDamageDescription('');
        setDamageLocation('');
        setDamageAssigneeUserId('');
        setDamageDate(new Date().toLocaleDateString('en-US'));
        fetchBoatDamageReports(selectedBoat.id, damageStatusFilter);
        fetchDamageReportCounts();
        alert('Damage report submitted successfully');
      } else {
        setError(data.error || 'Failed to submit damage report');
      }
    } catch (err) {
      console.error('Failed to submit damage report:', err);
      setError('Failed to submit damage report');
    }
  };

  const handleUpdateDamageReport = async (reportId, newStatus, assigneeUserId = undefined) => {
    try {
      const payload = {
        ...(newStatus ? { status: newStatus } : {}),
        ...(assigneeUserId !== undefined
          ? { assigneeUserId: assigneeUserId ? parseInt(assigneeUserId, 10) : null }
          : {})
      };
      if (newStatus && resolutionNotes !== undefined) {
        payload.resolutionNotes = resolutionNotes;
      }

      const response = await fetch(`${API_URL}/api/boats/damage-reports/${reportId}`, {
        method: 'PUT',
        headers: getAuthHeaders(token),
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        setResolvingReport(null);
        setResolutionNotes('');
        fetchBoatDamageReports(selectedBoat.id, damageStatusFilter);
        fetchDamageReportCounts();
      } else {
        const data = await response.json();
        setError(data.error || 'Failed to update damage report');
      }
    } catch (err) {
      console.error('Failed to update damage report:', err);
      setError('Failed to update damage report');
    }
  };

  const handleDeleteDamageReport = async (reportId) => {
    if (!confirm('Are you sure you want to delete this damage report?')) return;

    try {
      const response = await fetch(`${API_URL}/api/boats/damage-reports/${reportId}`, {
        method: 'DELETE',
        headers: getAuthHeaders(token)
      });

      if (response.ok) {
        fetchBoatDamageReports(selectedBoat.id, damageStatusFilter);
        fetchDamageReportCounts();
      } else {
        const data = await response.json();
        setError(data.error || 'Failed to delete damage report');
      }
    } catch (err) {
      console.error('Failed to delete damage report:', err);
      setError('Failed to delete damage report');
    }
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'minor': return 'bg-yellow-100 text-yellow-800';
      case 'moderate': return 'bg-orange-100 text-orange-800';
      case 'severe': return 'bg-red-100 text-red-800';
      case 'critical': return 'bg-red-600 text-white';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'open': return 'bg-red-100 text-red-800';
      case 'in_progress': return 'bg-blue-100 text-blue-800';
      case 'resolved': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const fetchBoatReservations = async (boatId) => {
    try {
      const response = await fetch(`${API_URL}/api/boats/${boatId}/reservations`, {
        headers: getAuthHeaders(token)
      });
      const data = await response.json();
      setReservations(data.reservations || []);
    } catch (err) {
      console.error('Failed to fetch reservations:', err);
      setError('Failed to load reservations');
    }
  };

  const handleAddBoat = async (e) => {
    e.preventDefault();
    setError('');

    try {
      const response = await fetch(`${API_URL}/api/boats`, {
        method: 'POST',
        headers: getAuthHeaders(token),
        body: JSON.stringify({
          name: boatName,
          boatClass: boatClass,
          weightClass: weightClass || null,
          spanCm: boatSpanCm ? parseInt(boatSpanCm, 10) : null,
          ownershipType: ownershipType,
          ownerIds: ownershipType === 'private' ? ownerIds.map(id => parseInt(id)) : []
        })
      });

      const data = await response.json();

      if (response.ok) {
        setBoatName('');
        setBoatClass('');
        setWeightClass('');
        setBoatSpanCm('');
        setOwnershipType('club');
        setOwnerIds([]);
        setShowAddForm(false);
        fetchBoats();
      } else {
        setError(data.error || 'Failed to add boat');
      }
    } catch (err) {
      console.error('Failed to add boat:', err);
      setError('Failed to add boat');
    }
  };

  const handleEditBoat = (boat) => {
    setEditingBoat(boat);
    setBoatName(boat.name);
    setBoatClass(boat.boat_class);
    setWeightClass(boat.weight_class || '');
    setBoatSpanCm(boat.span_cm?.toString() || '');
    setOwnershipType(boat.ownership_type || 'club');
    // Use owners array if available, otherwise fall back to single owner_id
    if (boat.owners && boat.owners.length > 0) {
      setOwnerIds(boat.owners.map(o => o.user_id.toString()));
    } else if (boat.owner_id) {
      setOwnerIds([boat.owner_id.toString()]);
    } else {
      setOwnerIds([]);
    }
    setShowAddForm(true);
  };

  const handleUpdateBoat = async (e) => {
    e.preventDefault();
    setError('');

    if (!editingBoat) return;

    try {
      const response = await fetch(`${API_URL}/api/boats/${editingBoat.id}`, {
        method: 'PUT',
        headers: getAuthHeaders(token),
        body: JSON.stringify({
          name: boatName,
          boatClass: boatClass,
          weightClass: weightClass || null,
          spanCm: boatSpanCm ? parseInt(boatSpanCm, 10) : null,
          ownershipType: ownershipType,
          ownerIds: ownershipType === 'private' ? ownerIds.map(id => parseInt(id)) : []
        })
      });

      const data = await response.json();

      if (response.ok) {
        resetBoatForm();
        fetchBoats();
      } else {
        setError(data.error || 'Failed to update boat');
      }
    } catch (err) {
      console.error('Failed to update boat:', err);
      setError('Failed to update boat');
    }
  };

  const resetBoatForm = () => {
    setBoatName('');
    setBoatClass('');
    setWeightClass('');
    setBoatSpanCm('');
        setOwnershipType('club');
        setOwnerIds([]);
        setEditingBoat(null);
    setShowAddForm(false);
    setError('');
  };

  const handleDeleteBoat = async (boatId) => {
    if (!confirm('Are you sure you want to delete this boat? This will also delete all meter logs and reservations for this boat.')) {
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/boats/${boatId}`, {
        method: 'DELETE',
        headers: getAuthHeaders(token)
      });

      if (response.ok) {
        fetchBoats();
      } else {
        const data = await response.json();
        setError(data.error || 'Failed to delete boat');
      }
    } catch (err) {
      console.error('Failed to delete boat:', err);
      setError('Failed to delete boat');
    }
  };

  const handleRetireBoat = async (boatId) => {
    try {
      const response = await fetch(`${API_URL}/api/boats/${boatId}/retire`, {
        method: 'POST',
        headers: getAuthHeaders(token),
        body: JSON.stringify({ notes: retireNotes })
      });

      if (response.ok) {
        setShowRetireModal(null);
        setRetireNotes('');
        fetchBoats(showRetired);
      } else {
        const data = await response.json();
        setError(data.error || 'Failed to retire equipment');
      }
    } catch (err) {
      console.error('Failed to retire equipment:', err);
      setError('Failed to retire equipment');
    }
  };

  const handleUnretireBoat = async (boatId) => {
    try {
      const response = await fetch(`${API_URL}/api/boats/${boatId}/unretire`, {
        method: 'POST',
        headers: getAuthHeaders(token)
      });

      if (response.ok) {
        fetchBoats(showRetired);
      } else {
        const data = await response.json();
        setError(data.error || 'Failed to reactivate equipment');
      }
    } catch (err) {
      console.error('Failed to reactivate equipment:', err);
      setError('Failed to reactivate equipment');
    }
  };

  // ==================== OAR HANDLERS ====================

  const handleAddOar = async (e) => {
    e.preventDefault();
    setError('');

    try {
      const response = await fetch(`${API_URL}/api/oars`, {
        method: 'POST',
        headers: getAuthHeaders(token),
        body: JSON.stringify({
          name: oarName,
          oarType: oarType,
          lengthCm: oarLengthCm ? parseInt(oarLengthCm) : null,
          inboardCm: oarInboardCm ? parseInt(oarInboardCm) : null,
          bladeType: oarBladeType || null,
          ownershipType: oarOwnershipType,
          ownerIds: oarOwnershipType === 'private' ? oarOwnerIds.map(id => parseInt(id)) : [],
          notes: oarNotes || null
        })
      });

      const data = await response.json();

      if (response.ok) {
        resetOarForm();
        fetchOars();
      } else {
        setError(data.error || 'Failed to add oars');
      }
    } catch (err) {
      console.error('Failed to add oars:', err);
      setError('Failed to add oars');
    }
  };

  const handleEditOar = (oar) => {
    setEditingOar(oar);
    setOarName(oar.name);
    setOarType(oar.oar_type);
    setOarLengthCm(oar.length_cm?.toString() || '');
    setOarInboardCm(oar.inboard_cm?.toString() || '');
    setOarBladeType(oar.blade_type || '');
    setOarOwnershipType(oar.ownership_type || 'club');
    if (oar.owners && oar.owners.length > 0) {
      setOarOwnerIds(oar.owners.map(o => o.user_id.toString()));
    } else if (oar.owner_id) {
      setOarOwnerIds([oar.owner_id.toString()]);
    } else {
      setOarOwnerIds([]);
    }
    setOarNotes(oar.notes || '');
    setShowAddOarForm(true);
  };

  const handleUpdateOar = async (e) => {
    e.preventDefault();
    setError('');

    if (!editingOar) return;

    try {
      const response = await fetch(`${API_URL}/api/oars/${editingOar.id}`, {
        method: 'PUT',
        headers: getAuthHeaders(token),
        body: JSON.stringify({
          name: oarName,
          oarType: oarType,
          lengthCm: oarLengthCm ? parseInt(oarLengthCm) : null,
          inboardCm: oarInboardCm ? parseInt(oarInboardCm) : null,
          bladeType: oarBladeType || null,
          ownershipType: oarOwnershipType,
          ownerIds: oarOwnershipType === 'private' ? oarOwnerIds.map(id => parseInt(id)) : [],
          notes: oarNotes || null
        })
      });

      const data = await response.json();

      if (response.ok) {
        resetOarForm();
        fetchOars();
      } else {
        setError(data.error || 'Failed to update oars');
      }
    } catch (err) {
      console.error('Failed to update oars:', err);
      setError('Failed to update oars');
    }
  };

  const resetOarForm = () => {
    setOarName('');
    setOarType('');
    setOarLengthCm('');
    setOarInboardCm('');
    setOarBladeType('');
    setOarOwnershipType('club');
    setOarOwnerIds([]);
    setOarNotes('');
    setEditingOar(null);
    setShowAddOarForm(false);
    setError('');
  };

  const handleDeleteOar = async (oarId) => {
    if (!confirm('Are you sure you want to delete these oars? This will also delete all damage reports and reservations.')) {
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/oars/${oarId}`, {
        method: 'DELETE',
        headers: getAuthHeaders(token)
      });

      if (response.ok) {
        fetchOars();
      } else {
        const data = await response.json();
        setError(data.error || 'Failed to delete oars');
      }
    } catch (err) {
      console.error('Failed to delete oars:', err);
      setError('Failed to delete oars');
    }
  };

  const handleRetireOar = async (oarId) => {
    try {
      const response = await fetch(`${API_URL}/api/oars/${oarId}/retire`, {
        method: 'POST',
        headers: getAuthHeaders(token),
        body: JSON.stringify({ notes: oarRetireNotes })
      });

      if (response.ok) {
        setShowOarRetireModal(null);
        setOarRetireNotes('');
        fetchOars(showRetiredOars);
      } else {
        const data = await response.json();
        setError(data.error || 'Failed to retire oars');
      }
    } catch (err) {
      console.error('Failed to retire oars:', err);
      setError('Failed to retire oars');
    }
  };

  const handleUnretireOar = async (oarId) => {
    try {
      const response = await fetch(`${API_URL}/api/oars/${oarId}/unretire`, {
        method: 'POST',
        headers: getAuthHeaders(token)
      });

      if (response.ok) {
        fetchOars(showRetiredOars);
      } else {
        const data = await response.json();
        setError(data.error || 'Failed to reactivate oars');
      }
    } catch (err) {
      console.error('Failed to reactivate oars:', err);
      setError('Failed to reactivate oars');
    }
  };

  const handleSplitOarSet = async (oar) => {
    const splitCountInput = prompt(
      `How many split sets should be created from "${oar.name}"?`,
      '2'
    );
    if (splitCountInput === null) return;
    const splitCount = parseInt(splitCountInput, 10);
    if (isNaN(splitCount) || splitCount < 2) {
      setError('Split count must be 2 or greater');
      return;
    }

    const confirmation = confirm(
      `Create ${splitCount} virtual split sets from "${oar.name}"?`
    );
    if (!confirmation) return;

    setError('');
    try {
      const response = await fetch(`${API_URL}/api/oars/${oar.id}/split`, {
        method: 'POST',
        headers: {
          ...getAuthHeaders(token),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ splitCount })
      });
      const data = await response.json();

      if (response.ok) {
        fetchOars(showRetiredOars);
      } else {
        setError(data.error || 'Failed to split oar set');
      }
    } catch (err) {
      console.error('Failed to split oar set:', err);
      setError('Failed to split oar set');
    }
  };

  const handleRecombineOarSet = async (oar) => {
    const confirmation = confirm(
      `Recombine active split sets back into "${oar.name}"?\n\nThis retires active split children for this set.`
    );
    if (!confirmation) return;

    setError('');
    try {
      const response = await fetch(`${API_URL}/api/oars/${oar.id}/recombine`, {
        method: 'POST',
        headers: getAuthHeaders(token)
      });
      const data = await response.json();

      if (response.ok) {
        fetchOars(showRetiredOars);
      } else {
        setError(data.error || 'Failed to recombine oar set');
      }
    } catch (err) {
      console.error('Failed to recombine oar set:', err);
      setError('Failed to recombine oar set');
    }
  };

  const handleViewOarDamageReports = async (oar) => {
    setSelectedOar(oar);
    try {
      const response = await fetch(`${API_URL}/api/oars/${oar.id}/damage-reports?status=${oarDamageStatusFilter}`, {
        headers: getAuthHeaders(token)
      });
      const data = await response.json();
      setOarDamageReports(data.reports || []);
      setShowOarDamageReports(true);
    } catch (err) {
      console.error('Failed to fetch oar damage reports:', err);
    }
  };

  const handleCreateOarDamageReport = async (e) => {
    e.preventDefault();
    setError('');

    try {
      const response = await fetch(`${API_URL}/api/oars/${selectedOar.id}/damage-reports`, {
        method: 'POST',
        headers: getAuthHeaders(token),
        body: JSON.stringify({
          severity: oarDamageSeverity,
          description: oarDamageDescription,
          locationOnOar: oarDamageLocation || null,
          discoveredDate: oarDamageDate
        })
      });

      if (response.ok) {
        setOarDamageSeverity('minor');
        setOarDamageDescription('');
        setOarDamageLocation('');
        setOarDamageDate(new Date().toLocaleDateString('en-US'));
        setShowAddOarDamageReport(false);
        handleViewOarDamageReports(selectedOar);
        fetchOarDamageReportCounts();
      } else {
        const data = await response.json();
        setError(data.error || 'Failed to create damage report');
      }
    } catch (err) {
      console.error('Failed to create damage report:', err);
      setError('Failed to create damage report');
    }
  };

  const handleResolveOarDamageReport = async (reportId) => {
    try {
      const response = await fetch(`${API_URL}/api/oars/damage-reports/${reportId}`, {
        method: 'PUT',
        headers: getAuthHeaders(token),
        body: JSON.stringify({
          status: 'resolved',
          resolutionNotes: oarResolutionNotes
        })
      });

      if (response.ok) {
        setResolvingOarReport(null);
        setOarResolutionNotes('');
        handleViewOarDamageReports(selectedOar);
        fetchOarDamageReportCounts();
      } else {
        const data = await response.json();
        setError(data.error || 'Failed to resolve damage report');
      }
    } catch (err) {
      console.error('Failed to resolve damage report:', err);
      setError('Failed to resolve damage report');
    }
  };

  // ==================== END OAR HANDLERS ====================

  const assigneeMetersSourceLabel = (source) => {
    if (source === 'training') return 'Training / other';
    if (source === 'boat') return 'Boat log';
    return 'Other';
  };

  const submitMetersLog = async (meterData, skipBoatTotal = false) => {
    const {
      meters: metersToLog,
      date: dateToLog,
      notes: notesToLog,
      boat: boatToLog,
      assignedUserIds,
      reservationId,
      acknowledgeAssigneeOverlap
    } = meterData;
    try {
      const payload = {
        boatId: boatToLog.id,
        meters: parseInt(metersToLog, 10),
        date: dateToLog,
        notes: notesToLog,
        skipBoatTotal
      };
      // Support multiple user assignment
      if (assignedUserIds && assignedUserIds.length > 0) {
        payload.assignedUserIds = assignedUserIds.map((id) => parseInt(id, 10));
      }
      if (reservationId) {
        payload.reservationId = reservationId;
      }
      if (acknowledgeAssigneeOverlap) {
        payload.acknowledgeAssigneeOverlap = true;
      }
      const response = await fetch(`${API_URL}/api/boats/meters`, {
        method: 'POST',
        headers: getAuthHeaders(token),
        body: JSON.stringify(payload)
      });

      const data = await response.json().catch(() => ({}));

      if (response.ok) {
        setMeters('');
        setMetersNotes('');
        setMetersAssignedUserIds([]);
        setShowUserSelector(false);
        setPendingMeterData(null);
        setShowDuplicatePrompt(false);
        setExistingLogs([]);
        setShowAssigneeOverlapPrompt(false);
        setAssigneeOverlapList([]);
        setPendingOverlapSubmit(null);
        setMetersSuccess('Meters saved successfully.');
        setMeterLogReservationId(null);
        fetchBoats();
        fetchMeterReminders();
        if (showMetersLog) {
          fetchMetersLog(boatToLog.id);
        }
      } else {
        if (
          response.status === 409 &&
          data.code === 'ASSIGNEE_OVERLAP' &&
          Array.isArray(data.assigneeOverlap) &&
          data.assigneeOverlap.length > 0
        ) {
          setAssigneeOverlapList(data.assigneeOverlap);
          setPendingOverlapSubmit({ meterData, skipBoatTotal });
          setShowAssigneeOverlapPrompt(true);
        } else {
          setError(data.error || 'Failed to log meters');
        }
      }
    } catch (err) {
      console.error('Failed to log meters:', err);
      setError('Failed to log meters');
    }
  };

  const proceedToAssigneeCheck = async (meterData, skipBoatTotal) => {
    const { date: dateToLog, boat: boatToLog, assignedUserIds, reservationId } = meterData;
    try {
      const params = new URLSearchParams({ boatId: String(boatToLog.id), date: dateToLog });
      if (assignedUserIds?.length) {
        params.set('userIds', assignedUserIds.map((id) => String(id)).join(','));
      }
      if (reservationId) {
        params.set('reservationId', String(reservationId));
      }
      const checkResponse = await fetch(
        `${API_URL}/api/boats/meters/check?${params.toString()}`,
        { headers: getAuthHeaders(token) }
      );
      const checkData = await checkResponse.json().catch(() => ({}));
      if (!checkResponse.ok) {
        setError(checkData.error || 'Failed to verify personal meters for the selected people.');
        return;
      }
      const overlap = checkData.assigneeOverlap;
      if (Array.isArray(overlap) && overlap.length > 0) {
        setPendingOverlapSubmit({ meterData, skipBoatTotal });
        setAssigneeOverlapList(overlap);
        setShowAssigneeOverlapPrompt(true);
        return;
      }
      await submitMetersLog(meterData, skipBoatTotal);
    } catch (err) {
      console.error('Assignee overlap check failed:', err);
      setError('Failed to verify personal meters. Please try again.');
    }
  };

  const handleLogMeters = async (e, addAnother = false) => {
    e?.preventDefault();
    if (editingLog) {
      setError('Finish or cancel editing the log entry in the list below before logging new meters.');
      return;
    }
    setError('');
    setMetersSuccess('');

    const meterData = {
      meters: parseInt(meters, 10),
      date: metersDate,
      notes: metersNotes,
      boat: selectedBoat,
      addAnother,
      assignedUserIds: metersAssignedUserIds.length > 0 ? metersAssignedUserIds : undefined,
      reservationId: meterLogReservationId || undefined
    };
    const { meters: metersToLog, date: dateToLog, boat: boatToLog } = meterData;

    if (!metersToLog || isNaN(metersToLog) || metersToLog <= 0) {
      setError('Please enter a valid number of meters');
      return;
    }

    // First step is always duplicate detection; writes happen only after this gate.
    try {
      const checkResponse = await fetch(
        `${API_URL}/api/boats/meters/check?boatId=${boatToLog.id}&date=${encodeURIComponent(dateToLog)}`,
        { headers: getAuthHeaders(token) }
      );
      const checkData = await checkResponse.json();
      
      if (checkData.hasExisting) {
        // Stage data and wait for explicit confirmation in the duplicate modal.
        setPendingMeterData(meterData);
        setExistingLogs(checkData.existingLogs);
        setShowDuplicatePrompt(true);
        return;
      }
    } catch (err) {
      console.error('Failed to check for duplicates:', err);
      setError('Failed to verify duplicate meters. Please try again.');
      return;
    }

    await proceedToAssigneeCheck(meterData, false);
  };

  const handleDuplicateChoice = async (isSameSession) => {
    if (!pendingMeterData) return;
    const data = { ...pendingMeterData };
    setShowDuplicatePrompt(false);
    setExistingLogs([]);
    setPendingMeterData(null);
    if (isSameSession) {
      await proceedToAssigneeCheck(data, true);
    } else {
      await proceedToAssigneeCheck(data, false);
    }
  };

  const cancelAssigneeOverlapPrompt = () => {
    setShowAssigneeOverlapPrompt(false);
    setAssigneeOverlapList([]);
    setPendingOverlapSubmit(null);
  };

  const confirmAssigneeOverlap = async () => {
    if (!pendingOverlapSubmit) return;
    const { meterData, skipBoatTotal } = pendingOverlapSubmit;
    await submitMetersLog({ ...meterData, acknowledgeAssigneeOverlap: true }, skipBoatTotal);
  };

  const cancelDuplicatePrompt = () => {
    setShowDuplicatePrompt(false);
    setPendingMeterData(null);
    setExistingLogs([]);
  };

  const fetchMetersLog = async (boatId) => {
    try {
      const response = await fetch(`${API_URL}/api/boats/${boatId}/meters`, {
        headers: getAuthHeaders(token)
      });
      const data = await response.json();
      setMetersLog(collapseReservationMeterLogs(data.logs || []));
    } catch (err) {
      console.error('Failed to fetch meters log:', err);
      setError('Failed to load meters log');
    }
  };

  const handleViewMetersLog = (boat) => {
    setSelectedBoat(boat);
    setShowMetersLog(true);
    setShowReservations(false);
    // If this boat has an open meter reminder, pre-fill the reservation's date and crew and
    // link the reservation, so logging auto-credits the crew and clears the reminder.
    const reminder = meterReminders.find((r) => r.boat_id === boat.id);
    if (reminder) {
      setMetersDate(reminder.reservation_date);
      setMeterLogReservationId(reminder.id);
      const fromEnriched = reminder.assignedUsers?.map((u) => String(u.id)) || [];
      const fromRaw = Array.isArray(reminder.assigned_user_ids)
        ? reminder.assigned_user_ids.map(String)
        : [];
      setMetersAssignedUserIds(fromEnriched.length > 0 ? fromEnriched : fromRaw);
    } else {
      setMeterLogReservationId(null);
      setMetersAssignedUserIds([]);
    }
    fetchMetersLog(boat.id);
  };

  const handleViewReservations = (boat) => {
    setSelectedBoat(boat);
    setShowReservations(true);
    setShowMetersLog(false);
    fetchBoatReservations(boat.id);
    setReservationDate(getLocalISODate());
    setStartTime(DEFAULT_RESERVATION_START_TIME);
    setEndTime(DEFAULT_RESERVATION_END_TIME);
    setJustReservedId(null);
  };

  const handleDeleteMetersLog = async (logId) => {
    if (!confirm('Are you sure you want to delete this log entry?')) {
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/boats/meters/${logId}`, {
        method: 'DELETE',
        headers: getAuthHeaders(token)
      });

      if (response.ok) {
        fetchBoats();
        if (selectedBoat?.id) fetchMetersLog(selectedBoat.id);
        fetchMyMetersLogs();
      } else {
        const data = await response.json();
        setError(data.error || 'Failed to delete log entry');
      }
    } catch (err) {
      console.error('Failed to delete log entry:', err);
      setError('Failed to delete log entry');
    }
  };

  // Check if user can edit a specific log entry
  const canEditLog = (log) => {
    // Admins and coaches can edit any log
    if (isCoach()) return true;
    // Users can only edit their own logs
    return log.logged_by === user?.id;
  };

  const handleStartEdit = (log) => {
    setEditingLog(log);
    setEditMeters(log.meters.toString());
    setEditDate(log.date);
    setEditNotes(log.notes || '');
    setEditAssignedUserId(log.assigned_to_user_id != null ? String(log.assigned_to_user_id) : '');
  };

  const handleCancelEdit = () => {
    setEditingLog(null);
    setEditMeters('');
    setEditDate('');
    setEditNotes('');
    setEditAssignedUserId('');
  };

  const handleUpdateMetersLog = async (e) => {
    e.preventDefault();
    setError('');

    if (!editMeters || isNaN(editMeters) || parseInt(editMeters) <= 0) {
      setError('Please enter a valid number of meters');
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/boats/meters/${editingLog.id}`, {
        method: 'PUT',
        headers: getAuthHeaders(token),
        body: JSON.stringify({
          meters: parseInt(editMeters),
          date: editDate,
          notes: editNotes,
          assignedUserId: editAssignedUserId === '' ? null : parseInt(editAssignedUserId, 10)
        })
      });

      const data = await response.json();

      if (response.ok) {
        handleCancelEdit();
        fetchBoats();
        if (selectedBoat?.id) fetchMetersLog(selectedBoat.id);
        fetchMyMetersLogs();
      } else {
        setError(data.error || 'Failed to update log entry');
      }
    } catch (err) {
      console.error('Failed to update log entry:', err);
      setError('Failed to update log entry');
    }
  };

  const handleCreateReservation = async (e) => {
    e.preventDefault();
    if (reservationInFlightRef.current) return;
    setError('');

    if (!reservationDate || !startTime || !endTime) {
      setError('Please fill in all required fields');
      return;
    }

    const formattedDate = isoDateToReservationDate(reservationDate);
    if (findOverlappingReservations(reservations, formattedDate, startTime, endTime).length > 0) {
      setError('That time overlaps a reservation already on this boat. Pick a free time.');
      return;
    }

    reservationInFlightRef.current = true;
    setSubmittingReservation(true);
    try {
      const response = await fetch(`${API_URL}/api/boats/reservations`, {
        method: 'POST',
        headers: getAuthHeaders(token),
        body: JSON.stringify({
          boatId: selectedBoat.id,
          reservationDate: formattedDate,
          startTime,
          endTime,
          purpose,
          ...(reservationAssignedUserIds.length > 0 && {
            assignedUserIds: reservationAssignedUserIds.map((id) => parseInt(id, 10))
          })
        })
      });

      const data = await response.json();

      if (response.ok) {
        setPurpose('');
        setReservationAssignedUserIds([]);
        setJustReservedId(data.reservation?.id ?? null);
        await fetchBoatReservations(selectedBoat.id);
        fetchMyReservations();
        fetchClubCalendarReservations(calendarMonth);
        alert('Reservation created successfully!');
      } else {
        setError(data.error || 'Failed to create reservation');
        await fetchBoatReservations(selectedBoat.id);
      }
    } catch (err) {
      console.error('Failed to create reservation:', err);
      setError('Failed to create reservation');
    } finally {
      reservationInFlightRef.current = false;
      setSubmittingReservation(false);
    }
  };

  const handleCancelReservation = async (reservationId) => {
    if (!confirm('Are you sure you want to cancel this reservation?')) {
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/boats/reservations/${reservationId}`, {
        method: 'DELETE',
        headers: getAuthHeaders(token)
      });

      if (response.ok) {
        if (selectedBoat) {
          fetchBoatReservations(selectedBoat.id);
        }
        fetchMyReservations();
        fetchClubCalendarReservations(calendarMonth);
      } else {
        const data = await response.json();
        setError(data.error || 'Failed to cancel reservation');
      }
    } catch (err) {
      console.error('Failed to cancel reservation:', err);
      setError('Failed to cancel reservation');
    }
  };

  const saveReservationCrew = async () => {
    if (!editingCrewForReservation) return;
    setError('');
    try {
      const response = await fetch(
        `${API_URL}/api/boats/reservations/${editingCrewForReservation.id}`,
        {
          method: 'PATCH',
          headers: getAuthHeaders(token),
          body: JSON.stringify({
            assignedUserIds: editingCrewUserIds.map((id) => parseInt(id, 10))
          })
        }
      );
      const data = await response.json();
      if (response.ok) {
        setEditingCrewForReservation(null);
        setEditingCrewUserIds([]);
        if (selectedBoat) fetchBoatReservations(selectedBoat.id);
        fetchMyReservations();
        fetchClubCalendarReservations(calendarMonth);
      } else {
        setError(data.error || 'Failed to update crew list');
      }
    } catch (e) {
      console.error('Failed to update crew list:', e);
      setError('Failed to update crew list');
    }
  };

  const formatNumber = (num) => {
    return new Intl.NumberFormat().format(num || 0);
  };

  const formatTime = (time) => {
    const [hours, minutes] = time.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const hour12 = hour % 12 || 12;
    return `${hour12}:${minutes} ${ampm}`;
  };

  const reservationHoldsOnDate = reservationsOnDate(reservations, isoDateToReservationDate(reservationDate));
  const reservationOverlaps = findOverlappingReservations(
    reservations,
    isoDateToReservationDate(reservationDate),
    startTime,
    endTime
  );
  // The booking just made overlaps the still-selected window; it isn't a conflict to warn about.
  const justBookedHold = reservationOverlaps.find((r) => r.id === justReservedId) || null;
  const reservationConflicts = reservationOverlaps.filter((r) => r.id !== justReservedId);

  const canReserveBoat = (boat) => {
    // Club boats can be reserved by anyone
    if (boat.ownership_type === 'club') return true;
    // Private boats can only be reserved by their owners (check owners array or legacy owner_id)
    if (boat.owners && boat.owners.length > 0) {
      return boat.owners.some(o => o.user_id === user?.id);
    }
    return boat.owner_id === user?.id;
  };

  const canEditBoatRigging = (boat) => {
    if (!boat) return false;
    if (boat.ownership_type === 'club') return true;
    if (boat.owners?.length > 0) return boat.owners.some(o => o.user_id === user?.id);
    return boat.owner_id === user?.id;
  };

  const openBoatRiggingForm = (boat) => {
    setEditingBoatRigging(boat);
    setBoatSpanCm(boat?.span_cm?.toString() || '');
    setShowBoatRiggingForm(true);
  };

  const saveBoatRigging = async (e) => {
    e.preventDefault();
    if (!editingBoatRigging) return;
    setError('');
    try {
      const response = await fetch(`${API_URL}/api/boats/${editingBoatRigging.id}/rigging`, {
        method: 'PATCH',
        headers: getAuthHeaders(token),
        body: JSON.stringify({
          spanCm: boatSpanCm ? parseInt(boatSpanCm, 10) : null
        })
      });
      const data = await response.json();
      if (!response.ok) {
        setError(data.error || 'Failed to update rigging');
        return;
      }
      setShowBoatRiggingForm(false);
      setEditingBoatRigging(null);
      fetchBoats(showRetired);
    } catch (err) {
      console.error('Failed to update boat rigging:', err);
      setError('Failed to update rigging');
    }
  };

  const canEditOarRigging = (oar) => {
    if (!oar) return false;
    if (oar.ownership_type === 'club') return true;
    if (isCoach() || isAdmin()) return true;
    if (oar.owners?.length > 0) return oar.owners.some(owner => owner.user_id === user?.id);
    return oar.owner_id === user?.id;
  };

  const openOarRiggingForm = (oar) => {
    setEditingOarRigging(oar);
    setOarLengthCm(oar.length_cm?.toString() || '');
    setOarInboardCm(oar.inboard_cm?.toString() || '');
    setOarBladeType(oar.blade_type || '');
    setShowOarRiggingForm(true);
  };

  const saveOarRigging = async (e) => {
    e.preventDefault();
    if (!editingOarRigging) return;
    setError('');
    try {
      const response = await fetch(`${API_URL}/api/oars/${editingOarRigging.id}/rigging`, {
        method: 'PATCH',
        headers: getAuthHeaders(token),
        body: JSON.stringify({
          lengthCm: oarLengthCm ? parseInt(oarLengthCm, 10) : null,
          inboardCm: oarInboardCm ? parseInt(oarInboardCm, 10) : null,
          bladeType: oarBladeType || null
        })
      });
      const data = await response.json();
      if (!response.ok) {
        setError(data.error || 'Failed to update oar rigging');
        return;
      }
      setShowOarRiggingForm(false);
      setEditingOarRigging(null);
      fetchOars(showRetiredOars);
    } catch (err) {
      console.error('Failed to update oar rigging:', err);
      setError('Failed to update oar rigging');
    }
  };

  const getOwnershipBadge = (boat) => {
    if (boat.ownership_type === 'private') {
      return (
        <span className="px-2 py-1 text-xs rounded-full bg-purple-100 text-purple-800">
          Private {boat.owner_name ? `(${boat.owner_name})` : ''}
        </span>
      );
    }
    return (
      <span className="px-2 py-1 text-xs rounded-full bg-green-100 text-green-800">
        Club
      </span>
    );
  };

  const reservationCalendarDays = useMemo(() => {
    const keys = [...new Set(clubCalendarReservations.map((r) => r.reservation_date))];
    return keys.map((mmddyyyy) => {
      const [mo, da, ye] = mmddyyyy.split('/').map(Number);
      return new Date(ye, mo - 1, da);
    });
  }, [clubCalendarReservations]);

  const reservationsForSelectedDay = useMemo(() => {
    const key = formatDateMMDDYYYYApi(selectedCalendarDay);
    return clubCalendarReservations
      .filter((r) => r.reservation_date === key)
      .sort((a, b) => (a.start_time || '').localeCompare(b.start_time || ''));
  }, [clubCalendarReservations, selectedCalendarDay]);

  const calendarTodayStart = useMemo(() => {
    const t = new Date();
    t.setHours(0, 0, 0, 0);
    return t;
  }, []);

  const canCancelClubReservation = (res) =>
    res.user_id === user?.id || isCoach() || isAdmin() || isSuperAdmin();

  if (loading) {
    return <div className="p-4 sm:p-6">Loading boats...</div>;
  }

  return (
    <div className="p-2 sm:p-6">
      {/* Tab Navigation */}
      <div className="flex gap-1 sm:gap-4 mb-4 sm:mb-6 border-b overflow-x-auto">
        <button
          onClick={() => setActiveTab('boats')}
          className={`pb-2 px-3 sm:px-4 font-semibold transition-colors whitespace-nowrap text-sm sm:text-base ${
            activeTab === 'boats'
              ? 'border-b-2 border-theme-primary text-theme-primary'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          Equipment
        </button>
        <button
          onClick={() => setActiveTab('oars')}
          className={`pb-2 px-3 sm:px-4 font-semibold transition-colors whitespace-nowrap text-sm sm:text-base ${
            activeTab === 'oars'
              ? 'border-b-2 border-theme-primary text-theme-primary'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          Oars
        </button>
        <button
          onClick={() => setActiveTab('reservations')}
          className={`pb-2 px-3 sm:px-4 font-semibold transition-colors whitespace-nowrap text-sm sm:text-base ${
            activeTab === 'reservations'
              ? 'border-b-2 border-theme-primary text-theme-primary'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          My Reservations {myReservations.length > 0 && `(${myReservations.length})`}
        </button>
        <button
          onClick={() => setActiveTab('upcomingSessions')}
          className={`pb-2 px-3 sm:px-4 font-semibold transition-colors whitespace-nowrap text-sm sm:text-base ${
            activeTab === 'upcomingSessions'
              ? 'border-b-2 border-theme-primary text-theme-primary'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          Upcoming Sessions
        </button>
        {isCoach() && (
          <button
            onClick={() => setActiveTab('massReserve')}
            className={`pb-2 px-3 sm:px-4 font-semibold transition-colors whitespace-nowrap text-sm sm:text-base ${
              activeTab === 'massReserve'
                ? 'border-b-2 border-theme-primary text-theme-primary'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            Mass Reserve
          </button>
        )}
        <button
          onClick={() => {
            setActiveTab('history');
            if (sessionHistory.length === 0) fetchSessionHistory();
          }}
          className={`pb-2 px-3 sm:px-4 font-semibold transition-colors whitespace-nowrap text-sm sm:text-base ${
            activeTab === 'history'
              ? 'border-b-2 border-theme-primary text-theme-primary'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          Session History
        </button>
        <button
          onClick={() => {
            setActiveTab('meterLogs');
            fetchMyMetersLogs();
          }}
          className={`pb-2 px-3 sm:px-4 font-semibold transition-colors whitespace-nowrap text-sm sm:text-base ${
            activeTab === 'meterLogs'
              ? 'border-b-2 border-theme-primary text-theme-primary'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          Meters log
        </button>
      </div>

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-3 sm:px-4 py-2 sm:py-3 rounded mb-4 text-sm sm:text-base">
          {error}
          <button onClick={() => setError('')} className="float-right font-bold">×</button>
        </div>
      )}

      {/* My Reservations Tab */}
      {activeTab === 'reservations' && (
        <div>
          <h2 className="text-xl sm:text-2xl font-bold text-theme-primary mb-4">My Upcoming Reservations</h2>
          {myReservations.length === 0 ? (
            <p className="text-gray-500 text-sm sm:text-base">No upcoming reservations. Select a boat to make a reservation.</p>
          ) : (
            <div className="space-y-3">
              {myReservations.map((res) => (
                <div key={res.id} className="border rounded-lg p-3 sm:p-4 flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 bg-white shadow-sm">
                  <div>
                    <div className="flex items-center gap-2">
                      <h4 className="font-bold text-theme-primary">{res.boat_name}</h4>
                      {res.user_id !== user?.id && (
                        <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-blue-100 text-blue-700">
                          Crew
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-gray-600">{res.boat_class}</p>
                    <p className="text-sm">
                      <span className="font-medium">{res.reservation_date}</span>
                      {' • '}
                      {formatTime(res.start_time)} - {formatTime(res.end_time)}
                    </p>
                    {res.purpose && <p className="text-sm text-gray-500 italic">{res.purpose}</p>}
                  </div>
                  {canCancelClubReservation(res) && (
                    <button
                      onClick={() => handleCancelReservation(res.id)}
                      className="text-red-600 hover:text-red-800 px-3 py-2 rounded border border-red-300 hover:bg-red-50 text-sm w-full sm:w-auto"
                    >
                      Cancel
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Upcoming Sessions — club-wide calendar */}
      {activeTab === 'upcomingSessions' && (
        <div>
          <h2 className="text-xl sm:text-2xl font-bold text-theme-primary mb-2">Upcoming Sessions</h2>
          <p className="text-gray-600 text-sm sm:text-base mb-4">
            Calendar of all upcoming boat reservations for your club. Select a day to see who reserved which equipment.
          </p>
          <div className="bg-white rounded-lg border shadow-sm overflow-hidden">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-0 lg:divide-x lg:divide-gray-200">
              <div className="p-4 sm:p-6 flex flex-col items-stretch">
                <div className="flex justify-between items-center mb-3 gap-2">
                  <span className="text-sm text-gray-600">
                    Days with reservations are highlighted.
                  </span>
                  <button
                    type="button"
                    onClick={() => fetchClubCalendarReservations(calendarMonth)}
                    className="text-sm text-theme-primary hover:underline shrink-0"
                  >
                    Refresh
                  </button>
                </div>
                {calendarLoading && clubCalendarReservations.length === 0 ? (
                  <p className="text-gray-500 py-8 text-center">Loading calendar…</p>
                ) : (
                  <div className="flex justify-center lg:justify-start overflow-x-auto">
                    <DayPicker
                      mode="single"
                      month={calendarMonth}
                      onMonthChange={handleCalendarMonthChange}
                      selected={selectedCalendarDay}
                      onSelect={(d) => {
                        if (d) {
                          const x = new Date(d);
                          x.setHours(0, 0, 0, 0);
                          setSelectedCalendarDay(x);
                        }
                      }}
                      showOutsideDays
                      disabled={{ before: calendarTodayStart }}
                      modifiers={{ hasReservation: reservationCalendarDays }}
                      modifiersClassNames={{
                        hasReservation:
                          'font-semibold bg-theme-bg-secondary text-theme-primary rounded-md'
                      }}
                      className="rounded-lg border p-3 sm:p-4 bg-gray-50"
                    />
                  </div>
                )}
              </div>
              <div className="p-4 sm:p-6 bg-theme-bg-secondary/40 min-h-[280px]">
                <h3 className="text-sm font-semibold text-gray-800 mb-3">
                  {selectedCalendarDay.toLocaleDateString('en-US', {
                    weekday: 'long',
                    month: 'long',
                    day: 'numeric',
                    year: 'numeric'
                  })}
                </h3>
                {calendarLoading && reservationsForSelectedDay.length === 0 ? (
                  <p className="text-gray-500 text-sm">Loading…</p>
                ) : reservationsForSelectedDay.length === 0 ? (
                  <p className="text-gray-500 text-sm">No upcoming reservations on this day.</p>
                ) : (
                  <ul className="space-y-3 max-h-[min(420px,50vh)] overflow-y-auto pr-1">
                    {reservationsForSelectedDay.map((res) => (
                      <li
                        key={res.id}
                        className="border rounded-lg p-3 bg-white shadow-sm flex flex-col sm:flex-row sm:justify-between sm:items-start gap-2"
                      >
                        <div className="min-w-0">
                          <p className="font-bold text-theme-primary truncate">{res.boat_name}</p>
                          <p className="text-xs text-gray-600">{res.boat_class}</p>
                          <p className="text-sm">
                            {formatTime(res.start_time)} – {formatTime(res.end_time)}
                          </p>
                          <p className="text-sm text-gray-800">{res.user_name}</p>
                          {res.purpose && (
                            <p className="text-xs text-gray-500 italic mt-1">{res.purpose}</p>
                          )}
                        </div>
                        {canCancelClubReservation(res) && (
                          <button
                            type="button"
                            onClick={() => handleCancelReservation(res.id)}
                            className="text-red-600 hover:text-red-800 px-3 py-1.5 rounded border border-red-200 text-sm shrink-0 self-start"
                          >
                            Cancel
                          </button>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'massReserve' && isCoach() && (
        <MassBoatReservation
          token={token}
          boats={boats}
          canReserveBoat={canReserveBoat}
          onCreated={() => {
            fetchMyReservations();
            fetchClubCalendarReservations(calendarMonth);
          }}
        />
      )}

      {/* Session History Tab */}
      {activeTab === 'history' && (
        <div>
          <h2 className="text-xl sm:text-2xl font-bold text-theme-primary mb-1">My Session History</h2>
          <p className="text-sm text-gray-600 mb-4">
            Your holds and outings. When a meters log matches a completed reservation (same boat and date), it
            appears on that session card. Other logs still list separately.
          </p>
          
          {/* Date filters */}
          <div className="bg-gray-50 rounded-lg p-4 mb-4">
            <div className="flex flex-wrap gap-3 items-end">
              <div>
                <label className="block text-xs text-gray-600 mb-1">From</label>
                <input
                  type="date"
                  value={historyDateRange.startDate}
                  onChange={(e) => setHistoryDateRange(prev => ({ ...prev, startDate: e.target.value }))}
                  className="border rounded px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1">To</label>
                <input
                  type="date"
                  value={historyDateRange.endDate}
                  onChange={(e) => setHistoryDateRange(prev => ({ ...prev, endDate: e.target.value }))}
                  className="border rounded px-3 py-2 text-sm"
                />
              </div>
              <button
                onClick={() => {
                  const start = historyDateRange.startDate ? 
                    new Date(historyDateRange.startDate).toLocaleDateString('en-US') : '';
                  const end = historyDateRange.endDate ? 
                    new Date(historyDateRange.endDate).toLocaleDateString('en-US') : '';
                  fetchSessionHistory(start, end);
                }}
                className="bg-theme-primary text-white px-4 py-2 rounded text-sm hover:opacity-90"
              >
                Filter
              </button>
              <button
                onClick={() => {
                  setHistoryDateRange({ startDate: '', endDate: '' });
                  fetchSessionHistory();
                }}
                className="text-gray-600 px-3 py-2 text-sm hover:text-gray-800"
              >
                Clear
              </button>
            </div>
          </div>

          {historyLoading ? (
            <div className="text-center py-8 text-gray-500">Loading history...</div>
          ) : sessionHistory.length === 0 ? (
            <p className="text-gray-500 text-sm sm:text-base">No session history found.</p>
          ) : (
            <div className="space-y-3">
              {/* Summary */}
              <div className="bg-theme-bg-secondary rounded-lg p-4 mb-4">
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-center">
                  <div>
                    <div className="text-2xl font-bold text-theme-primary">{sessionHistory.length}</div>
                    <div className="text-xs text-gray-600">Total entries</div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-theme-primary">
                      {sessionHistory.filter(s => s.is_past).length}
                    </div>
                    <div className="text-xs text-gray-600">Past / logged</div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-theme-primary">
                      {sessionHistory.filter(s => !s.is_past && s.entry_type !== 'meters_log').length}
                    </div>
                    <div className="text-xs text-gray-600">Upcoming holds</div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-theme-primary">
                      {new Set(sessionHistory.map(s => s.reservation_date)).size}
                    </div>
                    <div className="text-xs text-gray-600">Unique Days</div>
                  </div>
                </div>
              </div>

              {/* Sessions list */}
              {sessionHistory.map((session) => (
                <div 
                  key={session.id} 
                  className={`border rounded-lg p-3 sm:p-4 flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 bg-white shadow-sm ${
                    session.entry_type === 'meters_log'
                      ? 'border-l-4 border-l-violet-500'
                      : session.is_past
                        ? 'border-l-4 border-l-green-500'
                        : 'border-l-4 border-l-blue-500'
                  }`}
                >
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      {session.entry_type === 'meters_log' ? (
                        <span className="text-xs px-2 py-0.5 rounded bg-violet-100 text-violet-800">
                          Meters log
                        </span>
                      ) : (
                        <span className={`text-xs px-2 py-0.5 rounded ${
                          session.is_past ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'
                        }`}>
                          {session.is_past ? 'Completed' : 'Upcoming'}
                        </span>
                      )}
                      <span className="text-xs text-gray-500">{session.ownership_type}</span>
                    </div>
                    <h4 className="font-bold text-theme-primary">{session.boat_name}</h4>
                    <p className="text-sm text-gray-600">{session.boat_class}</p>
                    <p className="text-sm">
                      <span className="font-medium">{session.reservation_date}</span>
                      {session.entry_type === 'meters_log' ? (
                        <>
                          {' • '}
                          <span className="font-semibold text-theme-primary">
                            {(session.meters || 0).toLocaleString()} m
                            {session.meters >= 1000 && (
                              <span className="text-gray-600 font-normal">
                                {' '}({(session.meters / 1000).toFixed(1)} km)
                              </span>
                            )}
                          </span>
                        </>
                      ) : (
                        <>
                          {' • '}
                          {formatTime(session.start_time)} - {formatTime(session.end_time)}
                        </>
                      )}
                    </p>
                    {session.purpose && <p className="text-sm text-gray-500 italic">{session.purpose}</p>}
                    {session.entry_type === 'reservation' &&
                      session.session_meters &&
                      session.session_meters.length > 0 && (
                        <div className="mt-2 pt-2 border-t border-gray-100 space-y-1">
                          {session.session_meters.map((sm) => (
                            <p key={sm.boat_meters_id || sm.id} className="text-sm text-theme-primary font-medium">
                              Logged: {(sm.meters || 0).toLocaleString()} m
                              {sm.meters >= 1000 && (
                                <span className="text-gray-600 font-normal">
                                  {' '}({(sm.meters / 1000).toFixed(1)} km)
                                </span>
                              )}
                              {sm.notes || sm.purpose ? (
                                <span className="text-gray-600 font-normal"> — {sm.notes || sm.purpose}</span>
                              ) : null}
                            </p>
                          ))}
                        </div>
                      )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* My meters log history (entries you logged or were credited for) */}
      {activeTab === 'meterLogs' && (
        <div>
          <h2 className="text-xl sm:text-2xl font-bold text-theme-primary mb-1">My meters log</h2>
          <p className="text-sm text-gray-600 mb-4">
            Edit or delete entries the same way as on each boat&apos;s log. Session History shows matching logs on completed reservation cards.
          </p>
          <div className="flex justify-end mb-3">
            <button
              type="button"
              onClick={() => fetchMyMetersLogs()}
              className="text-sm text-theme-primary hover:underline"
            >
              Refresh
            </button>
          </div>
          {myMetersLogsLoading ? (
            <div className="text-center py-8 text-gray-500">Loading…</div>
          ) : myMetersLogsWeb.length === 0 ? (
            <p className="text-gray-500 text-sm sm:text-base">No meter logs yet. Log meters from the Equipment tab on a boat.</p>
          ) : (
            <div className="space-y-2 max-w-3xl">
              {myMetersLogsWeb.map((log) => (
                <div key={log.id} className="border p-3 rounded bg-white shadow-sm">
                  {editingLog?.id === log.id ? (
                    <form onSubmit={handleUpdateMetersLog} className="space-y-3">
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                        <div>
                          <label className="block text-xs font-semibold mb-1">Meters</label>
                          <input
                            type="number"
                            value={editMeters}
                            onChange={(e) => setEditMeters(e.target.value)}
                            className="border p-3 rounded w-full text-base"
                            required
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-semibold mb-1">Date</label>
                          <input
                            type="date"
                            value={
                              editDate.split('/').length === 3
                                ? `${editDate.split('/')[2]}-${editDate.split('/')[0].padStart(2, '0')}-${editDate.split('/')[1].padStart(2, '0')}`
                                : editDate
                            }
                            onChange={(e) => {
                              const [y, m, d] = e.target.value.split('-');
                              setEditDate(`${m}/${d}/${y}`);
                            }}
                            className="border p-3 rounded w-full text-base"
                            required
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-semibold mb-1">Notes</label>
                          <input
                            type="text"
                            value={editNotes}
                            onChange={(e) => setEditNotes(e.target.value)}
                            className="border p-3 rounded w-full text-base"
                          />
                        </div>
                        <div className="sm:col-span-3">
                          <label className="block text-xs font-semibold mb-1">Credited to</label>
                          <select
                            value={editAssignedUserId}
                            onChange={(e) => setEditAssignedUserId(e.target.value)}
                            className="border p-3 rounded w-full text-base bg-white text-gray-900"
                          >
                            <option value="">— Unassigned —</option>
                            {teamMembers.map((m) => (
                              <option key={m.id} value={m.id}>
                                {m.name}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <button
                          type="submit"
                          className="btn-theme-primary px-4 py-2 rounded text-base flex-1 sm:flex-initial"
                        >
                          Save
                        </button>
                        <button
                          type="button"
                          onClick={handleCancelEdit}
                          className="bg-gray-300 text-gray-700 px-4 py-2 rounded text-base hover:bg-gray-400 flex-1 sm:flex-initial"
                        >
                          Cancel
                        </button>
                      </div>
                    </form>
                  ) : (
                    <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
                      <div>
                        <p className="text-sm font-semibold text-theme-primary">{log.boat_name || 'Boat'}</p>
                        <p className="text-xs text-gray-500">{log.boat_class}</p>
                        <p className="font-semibold text-lg mt-1">{formatNumber(log.meters)} meters</p>
                        <p className="text-sm text-gray-600">
                          {log.date} {log.notes && `• ${log.notes}`}
                        </p>
                        {(log.skip_boat_total === true || log.skipBoatTotal === true) && (
                          <p className="text-xs text-amber-700 mt-0.5">Not added to hull total</p>
                        )}
                        <p className="text-xs text-gray-500">Logged by: {log.logged_by_name || 'Unknown'}</p>
                        {(log.credited_to_names || log.assigned_to_name) && (
                          <p className="text-xs text-theme-primary font-medium mt-1">
                            Credited to: {log.credited_to_names || log.assigned_to_name}
                          </p>
                        )}
                      </div>
                      {canEditLog(log) && (
                        <div className="flex gap-2 items-center">
                          <button
                            type="button"
                            onClick={() => handleStartEdit(log)}
                            className="text-blue-600 hover:text-blue-800 text-sm px-3 py-2 rounded border border-blue-300 hover:bg-blue-50 flex-1 sm:flex-initial"
                          >
                            Edit
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDeleteMetersLog(log.id)}
                            className="text-red-600 hover:text-red-800 px-3 py-2 rounded border border-red-300 hover:bg-red-50 flex-1 sm:flex-initial"
                          >
                            Delete
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Oars Tab */}
      {activeTab === 'oars' && (
        <div>
          {/* Oars Header */}
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 mb-4 sm:mb-6">
            <div>
              <h2 className="text-lg sm:text-xl font-bold text-theme-primary">Oars</h2>
              <p className="text-xs sm:text-sm text-gray-500">Manage oars and track damage reports</p>
            </div>
            <div className="flex gap-2 flex-wrap">
              {(isCoach() || isAdmin()) && (
                <>
                  <label className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={showRetiredOars}
                      onChange={(e) => {
                        setShowRetiredOars(e.target.checked);
                        fetchOars(e.target.checked);
                      }}
                      className="rounded"
                    />
                    Show Retired
                  </label>
                  <button
                    onClick={() => setShowAddOarForm(true)}
                    className="btn-theme-primary px-3 sm:px-4 py-2 rounded text-sm"
                  >
                    Add Oars
                  </button>
                </>
              )}
            </div>
          </div>

          {/* Oars Grid */}
          {oarsLoading ? (
            <p className="text-gray-500">Loading oars...</p>
          ) : (
            (() => {
              const activeOars = oars.filter(o => !o.is_retired);
              const retiredOars = oars.filter(o => o.is_retired);

              const renderOarCard = (oar) => (
                <div
                  key={oar.id}
                  className={`border rounded-lg p-3 sm:p-4 ${oar.is_retired ? 'bg-gray-100 opacity-75' : 'bg-white'}`}
                >
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <h3 className={`font-semibold text-sm sm:text-base ${oar.is_retired ? 'text-gray-500' : ''}`}>
                        {oar.name}
                        {oar.is_retired && <span className="ml-2 text-xs bg-gray-300 text-gray-700 px-2 py-0.5 rounded">RETIRED</span>}
                        {oar.is_virtual_split && !oar.is_retired && (
                          <span className="ml-2 text-xs bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded">
                            SPLIT {oar.split_label || ''}
                          </span>
                        )}
                      </h3>
                      <p className="text-xs sm:text-sm text-gray-600">
                        {OAR_TYPES.find(t => t.value === oar.oar_type)?.label || oar.oar_type}
                        {oar.blade_type && ` - ${oar.blade_type}`}
                      </p>
                      {(oar.length_cm || oar.inboard_cm) && (
                        <p className="text-xs text-gray-500">
                          {oar.length_cm && `${oar.length_cm}cm`}
                          {oar.length_cm && oar.inboard_cm && ' / '}
                          {oar.inboard_cm && `${oar.inboard_cm}cm inboard`}
                        </p>
                      )}
                    </div>
                    {oar.ownership_type === 'private' && (
                      <span className="text-xs bg-purple-100 text-purple-700 px-2 py-1 rounded">
                        Private
                      </span>
                    )}
                  </div>
                  
                  {oar.ownership_type === 'private' && oar.owner_name && (
                    <p className="text-xs text-gray-500 mb-2">Owner: {oar.owner_name}</p>
                  )}
                  
                  {oar.notes && (
                    <p className="text-xs text-gray-500 mb-2 italic">{oar.notes}</p>
                  )}

                  {oar.derived_from_name && (
                    <p className="text-xs text-gray-500 mb-2">
                      Derived from: <span className="font-medium">{oar.derived_from_name}</span>
                    </p>
                  )}
                  
                  {oar.is_retired && oar.retirement_notes && (
                    <p className="text-xs text-gray-500 mb-2">
                      <span className="font-medium">Retired:</span> {oar.retirement_notes}
                    </p>
                  )}
                  
                  {/* Damage report badge */}
                  {oarDamageReportCounts[oar.id] > 0 && (
                    <div className="mb-2">
                      <span className="text-xs bg-red-100 text-red-700 px-2 py-1 rounded">
                        {oarDamageReportCounts[oar.id]} open damage report{oarDamageReportCounts[oar.id] > 1 ? 's' : ''}
                      </span>
                    </div>
                  )}
                  
                  {/* Actions */}
                  {!oar.is_retired && (
                    <div className="flex gap-2 mt-3">
                      <button
                        onClick={() => handleViewOarDamageReports(oar)}
                        className="flex-1 btn-theme-secondary px-2 py-1 rounded text-xs"
                      >
                        Damage Reports
                      </button>
                      {canEditOarRigging(oar) && (
                        <button
                          onClick={() => openOarRiggingForm(oar)}
                          className="px-2 py-1 rounded text-xs border border-indigo-300 text-indigo-700 hover:bg-indigo-50"
                          title="Edit Rigging"
                        >
                          Edit Rigging
                        </button>
                      )}
                      {(isCoach() || isAdmin()) && (
                        <>
                          {!oar.is_virtual_split && !oar.has_active_split_children && (
                            <button
                              onClick={() => handleSplitOarSet(oar)}
                              className="px-2 py-1 rounded text-xs border border-indigo-300 text-indigo-700 hover:bg-indigo-50"
                              title="Split set into multiple virtual sets"
                            >
                              Split Set
                            </button>
                          )}
                          {!oar.is_virtual_split && oar.has_active_split_children && (
                            <button
                              onClick={() => handleRecombineOarSet(oar)}
                              className="px-2 py-1 rounded text-xs border border-amber-300 text-amber-700 hover:bg-amber-50"
                              title="Recombine active split sets"
                            >
                              Recombine
                            </button>
                          )}
                          <button
                            onClick={() => handleEditOar(oar)}
                            className="p-1 text-gray-500 hover:text-theme-primary"
                            title="Edit"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                          </button>
                          <button
                            onClick={() => setShowOarRetireModal(oar)}
                            className="p-1 text-gray-500 hover:text-yellow-600"
                            title="Retire"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
                            </svg>
                          </button>
                          <button
                            onClick={() => handleDeleteOar(oar.id)}
                            className="p-1 text-gray-500 hover:text-red-600"
                            title="Delete"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        </>
                      )}
                    </div>
                  )}
                  
                  {oar.is_retired && (isCoach() || isAdmin()) && (
                    <div className="flex gap-2 mt-3">
                      <button
                        onClick={() => handleUnretireOar(oar.id)}
                        className="flex-1 btn-theme-secondary px-2 py-1 rounded text-xs"
                      >
                        Reactivate
                      </button>
                    </div>
                  )}
                </div>
              );

              return (
                <div className="space-y-6">
                  {/* Active Oars */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
                    {activeOars.length === 0 && retiredOars.length === 0 ? (
                      <p className="text-gray-500 col-span-full text-sm">
                        No oars added yet. {isCoach() || isAdmin() ? 'Click "Add Oars" to get started.' : ''}
                      </p>
                    ) : activeOars.length === 0 ? (
                      <p className="text-gray-500 col-span-full text-sm">
                        No active oars. {showRetiredOars && retiredOars.length > 0 ? 'See retired oars below.' : ''}
                      </p>
                    ) : (
                      activeOars.map(renderOarCard)
                    )}
                  </div>

                  {/* Retired Oars */}
                  {showRetiredOars && retiredOars.length > 0 && (isCoach() || isAdmin()) && (
                    <div className="mt-8 border-t pt-6">
                      <h3 className="text-lg font-semibold text-gray-500 mb-4 flex items-center gap-2">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
                        </svg>
                        Retired Oars ({retiredOars.length})
                      </h3>
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
                        {retiredOars.map(renderOarCard)}
                      </div>
                    </div>
                  )}
                </div>
              );
            })()
          )}
        </div>
      )}

      {/* Add Oar Modal */}
      <MobileModal
        isOpen={showAddOarForm}
        onClose={resetOarForm}
        title={editingOar ? 'Edit Oars' : 'Add Oars'}
        maxWidth="max-w-md"
      >
        <form onSubmit={editingOar ? handleUpdateOar : handleAddOar} className="space-y-4">
          <div>
            <label className="block text-sm font-semibold mb-1">Name *</label>
            <input
              type="text"
              value={oarName}
              onChange={(e) => setOarName(e.target.value)}
              placeholder="e.g., Concept2 Sculls #1"
              className="w-full border p-2 rounded text-theme-primary bg-white"
              required
            />
          </div>
          
          <div>
            <label className="block text-sm font-semibold mb-1">Type *</label>
            <select
              value={oarType}
              onChange={(e) => setOarType(e.target.value)}
              className="w-full border p-2 rounded text-theme-primary bg-white"
              required
            >
              <option value="">Select type...</option>
              {OAR_TYPES.map(type => (
                <option key={type.value} value={type.value}>{type.label}</option>
              ))}
            </select>
          </div>
          
          {/* Rigging Fields */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-semibold mb-1">Length (cm)</label>
              <input
                type="number"
                value={oarLengthCm}
                onChange={(e) => setOarLengthCm(e.target.value)}
                placeholder="e.g., 289"
                className="w-full border p-2 rounded text-theme-primary bg-white"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold mb-1">Inboard (cm)</label>
              <input
                type="number"
                value={oarInboardCm}
                onChange={(e) => setOarInboardCm(e.target.value)}
                placeholder="e.g., 88"
                className="w-full border p-2 rounded text-theme-primary bg-white"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-semibold mb-1">Blade Type</label>
            <input
              type="text"
              value={oarBladeType}
              onChange={(e) => setOarBladeType(e.target.value)}
              placeholder="e.g., Smoothie2, Fat2, Macon"
              className="w-full border p-2 rounded text-theme-primary bg-white"
            />
          </div>

          <div>
            <label className="block text-sm font-semibold mb-1">Ownership</label>
            <select
              value={oarOwnershipType}
              onChange={(e) => {
                setOarOwnershipType(e.target.value);
                if (e.target.value === 'club') setOarOwnerIds([]);
              }}
              className="w-full border p-2 rounded text-theme-primary bg-white"
            >
              <option value="club">Club</option>
              <option value="private">Private</option>
            </select>
          </div>
          
          {oarOwnershipType === 'private' && (
            <div>
              <label className="block text-sm font-semibold mb-1">Owner(s)</label>
              <select
                multiple
                value={oarOwnerIds}
                onChange={(e) => setOarOwnerIds(Array.from(e.target.selectedOptions, opt => opt.value))}
                className="w-full border p-2 rounded text-theme-primary bg-white h-32"
              >
                {teamMembers.map(member => (
                  <option key={member.id} value={member.id}>{member.name}</option>
                ))}
              </select>
              <p className="text-xs text-gray-500 mt-1">Hold Ctrl/Cmd to select multiple</p>
            </div>
          )}
          
          <div>
            <label className="block text-sm font-semibold mb-1">Notes</label>
            <textarea
              value={oarNotes}
              onChange={(e) => setOarNotes(e.target.value)}
              placeholder="Any additional notes..."
              className="w-full border p-2 rounded text-theme-primary bg-white"
              rows={2}
            />
          </div>
          
          <div className="flex gap-2 justify-end">
            <button type="button" onClick={resetOarForm} className="px-4 py-2 text-gray-600 hover:text-gray-800">
              Cancel
            </button>
            <button type="submit" className="btn-theme-primary px-4 py-2 rounded">
              {editingOar ? 'Update' : 'Add'} Oars
            </button>
          </div>
        </form>
      </MobileModal>

      {/* Oar Rigging Modal */}
      <MobileModal
        isOpen={showOarRiggingForm && !!editingOarRigging}
        onClose={() => {
          setShowOarRiggingForm(false);
          setEditingOarRigging(null);
        }}
        title={editingOarRigging ? `Edit Rigging - ${editingOarRigging.name}` : 'Edit Oar Rigging'}
        maxWidth="max-w-md"
      >
        {editingOarRigging && (
          <form onSubmit={saveOarRigging} className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-semibold mb-1">Length (cm)</label>
                <input
                  type="number"
                  value={oarLengthCm}
                  onChange={(e) => setOarLengthCm(e.target.value)}
                  className="w-full border p-2 rounded text-theme-primary bg-white"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold mb-1">Inboard (cm)</label>
                <input
                  type="number"
                  value={oarInboardCm}
                  onChange={(e) => setOarInboardCm(e.target.value)}
                  className="w-full border p-2 rounded text-theme-primary bg-white"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-semibold mb-1">Blade Type</label>
              <input
                type="text"
                value={oarBladeType}
                onChange={(e) => setOarBladeType(e.target.value)}
                className="w-full border p-2 rounded text-theme-primary bg-white"
              />
            </div>
            <div className="flex gap-2 justify-end">
              <button
                type="button"
                onClick={() => {
                  setShowOarRiggingForm(false);
                  setEditingOarRigging(null);
                }}
                className="px-4 py-2 text-gray-600 hover:text-gray-800"
              >
                Cancel
              </button>
              <button type="submit" className="btn-theme-primary px-4 py-2 rounded">
                Save Rigging
              </button>
            </div>
          </form>
        )}
      </MobileModal>

      {/* Oar Retire Modal */}
      <MobileModal
        isOpen={!!showOarRetireModal}
        onClose={() => {
          setShowOarRetireModal(null);
          setOarRetireNotes('');
        }}
        title="Retire Oars"
        maxWidth="max-w-md"
      >
        <div className="space-y-4">
          <p className="text-sm text-gray-600">
            Are you sure you want to retire <strong>{showOarRetireModal?.name}</strong>?
          </p>
          <p className="text-xs text-gray-500">
            Retired oars will not be available for lineups but will remain in historical reports.
          </p>
          <div>
            <label className="block text-sm font-semibold mb-1">Retirement Notes</label>
            <textarea
              value={oarRetireNotes}
              onChange={(e) => setOarRetireNotes(e.target.value)}
              placeholder="Reason for retirement..."
              className="w-full border p-2 rounded text-theme-primary bg-white"
              rows={2}
            />
          </div>
          <div className="flex gap-2 justify-end">
            <button
              onClick={() => {
                setShowOarRetireModal(null);
                setOarRetireNotes('');
              }}
              className="px-4 py-2 text-gray-600"
            >
              Cancel
            </button>
            <button
              onClick={() => handleRetireOar(showOarRetireModal.id)}
              className="px-4 py-2 bg-yellow-500 text-white rounded hover:bg-yellow-600"
            >
              Retire Oars
            </button>
          </div>
        </div>
      </MobileModal>

      {/* Oar Damage Reports Modal */}
      <MobileModal
        isOpen={showOarDamageReports}
        onClose={() => {
          setShowOarDamageReports(false);
          setSelectedOar(null);
          setOarDamageReports([]);
        }}
        title={`Damage Reports - ${selectedOar?.name || ''}`}
        maxWidth="max-w-2xl"
      >
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <select
              value={oarDamageStatusFilter}
              onChange={(e) => {
                setOarDamageStatusFilter(e.target.value);
                if (selectedOar) handleViewOarDamageReports({ ...selectedOar });
              }}
              className="border p-2 rounded text-sm"
            >
              <option value="all">All Reports</option>
              <option value="open">Open</option>
              <option value="in_progress">In Progress</option>
              <option value="resolved">Resolved</option>
            </select>
            <button
              onClick={() => setShowAddOarDamageReport(true)}
              className="btn-theme-primary px-3 py-1 rounded text-sm"
            >
              Report Damage
            </button>
          </div>

          {oarDamageReports.length === 0 ? (
            <p className="text-gray-500 text-sm text-center py-4">No damage reports found.</p>
          ) : (
            <div className="space-y-3 max-h-96 overflow-y-auto">
              {oarDamageReports.map(report => (
                <div key={report.id} className={`border rounded p-3 ${report.status === 'resolved' ? 'bg-gray-50' : ''}`}>
                  <div className="flex justify-between items-start mb-2">
                    <span className={`text-xs px-2 py-1 rounded ${
                      report.severity === 'critical' ? 'bg-red-100 text-red-700' :
                      report.severity === 'severe' ? 'bg-orange-100 text-orange-700' :
                      report.severity === 'moderate' ? 'bg-yellow-100 text-yellow-700' :
                      'bg-blue-100 text-blue-700'
                    }`}>
                      {report.severity}
                    </span>
                    <span className={`text-xs px-2 py-1 rounded ${
                      report.status === 'resolved' ? 'bg-green-100 text-green-700' :
                      report.status === 'in_progress' ? 'bg-yellow-100 text-yellow-700' :
                      'bg-gray-100 text-gray-700'
                    }`}>
                      {report.status}
                    </span>
                  </div>
                  <p className="text-sm mb-1">{report.description}</p>
                  {report.location_on_oar && (
                    <p className="text-xs text-gray-500">Location: {report.location_on_oar}</p>
                  )}
                  <p className="text-xs text-gray-500">
                    Reported by {report.reporter_name} on {report.discovered_date}
                  </p>
                  {report.resolution_notes && (
                    <p className="text-xs text-gray-500 mt-1 italic">Resolution: {report.resolution_notes}</p>
                  )}
                  
                  {report.status !== 'resolved' && (isCoach() || isAdmin()) && (
                    <div className="mt-2">
                      {resolvingOarReport === report.id ? (
                        <div className="space-y-2">
                          <textarea
                            value={oarResolutionNotes}
                            onChange={(e) => setOarResolutionNotes(e.target.value)}
                            placeholder="Resolution notes..."
                            className="w-full border p-2 rounded text-sm"
                            rows={2}
                          />
                          <div className="flex gap-2">
                            <button
                              onClick={() => handleResolveOarDamageReport(report.id)}
                              className="px-3 py-1 bg-green-500 text-white rounded text-xs"
                            >
                              Mark Resolved
                            </button>
                            <button
                              onClick={() => {
                                setResolvingOarReport(null);
                                setOarResolutionNotes('');
                              }}
                              className="px-3 py-1 text-gray-500 text-xs"
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      ) : (
                        <button
                          onClick={() => setResolvingOarReport(report.id)}
                          className="text-xs text-green-600 hover:underline"
                        >
                          Resolve
                        </button>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </MobileModal>

      {/* Add Oar Damage Report Modal */}
      <MobileModal
        isOpen={showAddOarDamageReport}
        onClose={() => {
          setShowAddOarDamageReport(false);
          setOarDamageSeverity('minor');
          setOarDamageDescription('');
          setOarDamageLocation('');
          setOarDamageDate(new Date().toLocaleDateString('en-US'));
        }}
        title="Report Oar Damage"
        maxWidth="max-w-md"
      >
        <form onSubmit={handleCreateOarDamageReport} className="space-y-4">
          <div>
            <label className="block text-sm font-semibold mb-1">Severity *</label>
            <select
              value={oarDamageSeverity}
              onChange={(e) => setOarDamageSeverity(e.target.value)}
              className="w-full border p-2 rounded text-theme-primary bg-white"
              required
            >
              <option value="minor">Minor - Cosmetic, doesn't affect use</option>
              <option value="moderate">Moderate - Affects performance</option>
              <option value="severe">Severe - Needs repair before use</option>
              <option value="critical">Critical - Unsafe to use</option>
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-semibold mb-1">Description *</label>
            <textarea
              value={oarDamageDescription}
              onChange={(e) => setOarDamageDescription(e.target.value)}
              placeholder="Describe the damage..."
              className="w-full border p-2 rounded text-theme-primary bg-white"
              rows={3}
              required
            />
          </div>
          
          <div>
            <label className="block text-sm font-semibold mb-1">Location on Oar</label>
            <input
              type="text"
              value={oarDamageLocation}
              onChange={(e) => setOarDamageLocation(e.target.value)}
              placeholder="e.g., blade, shaft, collar"
              className="w-full border p-2 rounded text-theme-primary bg-white"
            />
          </div>
          
          <div>
            <label className="block text-sm font-semibold mb-1">Date Discovered *</label>
            <input
              type="text"
              value={oarDamageDate}
              onChange={(e) => setOarDamageDate(e.target.value)}
              placeholder="MM/DD/YYYY"
              className="w-full border p-2 rounded text-theme-primary bg-white"
              required
            />
          </div>
          
          <div className="flex gap-2 justify-end">
            <button
              type="button"
              onClick={() => setShowAddOarDamageReport(false)}
              className="px-4 py-2 text-gray-600"
            >
              Cancel
            </button>
            <button type="submit" className="btn-theme-primary px-4 py-2 rounded">
              Submit Report
            </button>
          </div>
        </form>
      </MobileModal>

      {/* Equipment Tab */}
      {activeTab === 'boats' && (
        <>
          {/* Meter Reminders - Past reservations needing meter logging */}
          {meterReminders.length > 0 && (
            <div className="mb-6 space-y-3">
              <h3 className="text-sm font-semibold text-amber-800 flex items-center gap-2">
                <span>⏰</span> Log your meters
              </h3>
              <p className="text-xs text-amber-800/90 leading-relaxed -mt-1 mb-2">
                Past reservations: use Log Meters if you still need to enter distance, or Don&apos;t remind me if you already logged elsewhere or didn&apos;t go out.
              </p>
              {meterReminders.map((reminder) => (
                <div
                  key={reminder.id}
                  className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex flex-col sm:flex-row sm:items-center gap-3"
                >
                  <div className="flex-1">
                    <p className="font-semibold text-amber-900">
                      {reminder.boat_name} <span className="text-amber-700 font-normal">({reminder.boat_class})</span>
                    </p>
                    <p className="text-sm text-amber-700">
                      Reserved on {reminder.reservation_date} • {reminder.start_time} - {reminder.end_time}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => openMeterLogForReminder(reminder)}
                      className="flex-1 sm:flex-initial bg-amber-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-amber-700"
                    >
                      Log Meters
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowDismissConfirm(reminder.id)}
                      className="flex-1 sm:flex-initial bg-gray-200 text-gray-700 px-4 py-2 rounded-lg text-sm font-medium hover:bg-gray-300"
                    >
                      Don&apos;t remind me
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Dismiss Confirmation Modal */}
          <MobileModal
            isOpen={showDismissConfirm !== null}
            onClose={() => setShowDismissConfirm(null)}
            title="Stop this reminder?"
            maxWidth="max-w-sm"
          >
            <div className="text-center">
              <p className="text-gray-700 mb-6 text-left text-sm leading-relaxed">
                You won&apos;t see this nudge for this reservation again. Use this if you already logged meters from Log Meters, your training log, or elsewhere—or if you didn&apos;t go out and don&apos;t need a reminder.
              </p>
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => setShowDismissConfirm(null)}
                  className="flex-1 bg-gray-200 text-gray-700 px-4 py-3 rounded-lg font-medium hover:bg-gray-300"
                >
                  Not now
                </button>
                <button
                  type="button"
                  onClick={() => dismissMeterReminder(showDismissConfirm)}
                  className="flex-1 bg-amber-600 text-white px-4 py-3 rounded-lg font-medium hover:bg-amber-700"
                >
                  Don&apos;t remind me
                </button>
              </div>
            </div>
          </MobileModal>

          {/* Duplicate Detection Modal */}
          <MobileModal
            isOpen={showDuplicatePrompt}
            onClose={cancelDuplicatePrompt}
            title="Existing Meters Logged"
            maxWidth="max-w-md"
            zIndex={70}
          >
            <div>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                <p className="text-blue-800 text-sm">
                  <span className="font-semibold">
                    {existingLogs.every(log => log.is_own_log) 
                      ? "You've already logged meters" 
                      : "Meters have been logged"}
                  </span> for this equipment on {pendingMeterData?.date}:
                </p>
                <div className="mt-3 space-y-2">
                  {existingLogs.map((log, idx) => (
                    <div key={idx} className={`rounded p-2 text-sm ${log.is_own_log ? 'bg-amber-50 border border-amber-200' : 'bg-white'}`}>
                      <span className="font-medium">
                        {log.is_own_log ? 'You' : log.logger_name}
                      </span> logged{' '}
                      <span className="font-semibold text-blue-600">{log.meters.toLocaleString()}m</span>
                      {log.is_own_log && <span className="text-amber-600 text-xs ml-2">(your log)</span>}
                      {log.notes && <span className="text-gray-500 block text-xs mt-1">"{log.notes}"</span>}
                    </div>
                  ))}
                </div>
              </div>
              
              <p className="text-gray-700 mb-4 text-center">
                {existingLogs.every(log => log.is_own_log) 
                  ? <>Did you go out <strong>again</strong> on this equipment today?</>
                  : <>Is this the <strong>same outing</strong> (e.g., same crew)?</>
                }
              </p>
              
              <div className="space-y-3">
                <button
                  onClick={() => handleDuplicateChoice(true)}
                  className="w-full bg-gray-100 text-gray-800 px-4 py-3 rounded-lg font-medium hover:bg-gray-200 border border-gray-300"
                >
                  <span className="block font-semibold">
                    {existingLogs.every(log => log.is_own_log) 
                      ? "Oops, already logged" 
                      : "Yes, same session"}
                  </span>
                  <span className="text-xs text-gray-500">Log to my personal record only (boat total unchanged)</span>
                </button>
                <button
                  onClick={() => handleDuplicateChoice(false)}
                  className="w-full bg-theme-primary text-white px-4 py-3 rounded-lg font-medium hover:opacity-90"
                >
                  <span className="block font-semibold">
                    {existingLogs.every(log => log.is_own_log) 
                      ? "Yes, different outing" 
                      : "No, different outing"}
                  </span>
                  <span className="text-xs text-white/80">Add my meters to the boat's total</span>
                </button>
                <button
                  onClick={cancelDuplicatePrompt}
                  className="w-full text-gray-500 px-4 py-2 text-sm hover:text-gray-700"
                >
                  Cancel
                </button>
              </div>
            </div>
          </MobileModal>

          {/* Personal meters (assignee) overlap — e.g. training vs boat on same day */}
          <MobileModal
            isOpen={showAssigneeOverlapPrompt}
            onClose={cancelAssigneeOverlapPrompt}
            title="Some athletes already have personal meters this day"
            maxWidth="max-w-md"
            zIndex={71}
          >
            <div>
              <p className="text-gray-700 text-sm mb-3">
                The following people already have individual meter credits for{' '}
                {pendingOverlapSubmit?.meterData?.date || 'this date'}. Adding this log will give them
                an additional line (for example, training and a boat log on the same day). You can
                go back to change who is selected, or confirm to add the credit.
              </p>
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4 max-h-56 overflow-y-auto space-y-3 text-sm">
                {assigneeOverlapList.map((row) => (
                  <div key={row.userId}>
                    <p className="font-semibold text-amber-900">{row.name}</p>
                    <ul className="mt-1 space-y-1 pl-0 list-none">
                      {row.entries.map((e) => (
                        <li key={e.id} className="text-amber-900/90">
                          {e.meters != null && (
                            <span className="font-medium">{Number(e.meters).toLocaleString()}m</span>
                          )}{' '}
                          <span className="text-amber-800/80">
                            ({assigneeMetersSourceLabel(e.source)})
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
              <div className="space-y-3">
                <button
                  type="button"
                  onClick={confirmAssigneeOverlap}
                  className="w-full bg-theme-primary text-white px-4 py-3 rounded-lg font-medium hover:opacity-90"
                >
                  Confirm and add this credit
                </button>
                <button
                  type="button"
                  onClick={cancelAssigneeOverlapPrompt}
                  className="w-full text-gray-600 px-4 py-2 text-sm hover:text-gray-800"
                >
                  Go back
                </button>
              </div>
            </div>
          </MobileModal>

          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 mb-4 sm:mb-6">
            <h2 className="text-xl sm:text-2xl font-bold text-theme-primary">
              {isCoach() ? 'Equipment Management' : 'Equipment'}
            </h2>
            {isCoach() && (
              <button
                onClick={() => setShowAddForm(true)}
                className="btn-theme-primary px-4 py-2 sm:py-2 rounded-lg text-sm sm:text-base w-full sm:w-auto"
              >
                Add Equipment
              </button>
            )}
          </div>

          {/* Search and Filter Bar */}
          <div className="mb-4 sm:mb-6 space-y-3">
            <input
              type="text"
              placeholder="Search equipment by name..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full border p-3 rounded-lg text-base focus:outline-none focus:ring-2 focus:ring-theme-primary"
            />
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={showErgsOnly}
                onChange={(e) => {
                  setShowErgsOnly(e.target.checked);
                  if (e.target.checked) setShowLaunchesOnly(false);
                }}
                className="w-4 h-4 text-theme-primary rounded"
              />
              <span className="text-sm font-medium text-gray-700">Show Ergs Only</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={showLaunchesOnly}
                onChange={(e) => {
                  setShowLaunchesOnly(e.target.checked);
                  if (e.target.checked) setShowErgsOnly(false);
                }}
                className="w-4 h-4 text-theme-primary rounded"
              />
              <span className="text-sm font-medium text-gray-700">Show Launches Only</span>
            </label>
            
            {/* Show Retired Toggle - Coaches/Admins only */}
            {(isCoach() || isAdmin()) && (
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={showRetired}
                  onChange={(e) => {
                    setShowRetired(e.target.checked);
                    fetchBoats(e.target.checked);
                  }}
                  className="w-4 h-4 text-theme-primary rounded"
                />
                <span className="text-sm font-medium text-gray-700">Show Retired</span>
              </label>
            )}
          </div>

      {/* Retire Equipment Modal */}
      <MobileModal
        isOpen={showRetireModal !== null}
        onClose={() => {
          setShowRetireModal(null);
          setRetireNotes('');
        }}
        title="Retire Equipment"
        maxWidth="max-w-md"
      >
        {showRetireModal && (
          <div className="p-4">
            <p className="mb-4">
              Are you sure you want to retire <strong>{showRetireModal.name}</strong>?
            </p>
            <p className="text-sm text-gray-600 mb-4">
              Retired equipment will no longer appear for athletes and cannot be reserved. 
              It will still be visible in historical reports and can be reactivated later.
            </p>
            <div className="mb-4">
              <label className="block text-sm font-semibold mb-2">Retirement Notes (optional)</label>
              <textarea
                value={retireNotes}
                onChange={(e) => setRetireNotes(e.target.value)}
                className="border p-3 rounded w-full text-base"
                rows={3}
                placeholder="e.g., Hull damage beyond repair, Replaced by new equipment..."
              />
            </div>
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => {
                  setShowRetireModal(null);
                  setRetireNotes('');
                }}
                className="flex-1 px-4 py-2 border rounded hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => handleRetireBoat(showRetireModal.id)}
                className="flex-1 px-4 py-2 bg-amber-600 text-white rounded hover:bg-amber-700"
              >
                Retire Equipment
              </button>
            </div>
          </div>
        )}
      </MobileModal>

      {/* Add/Edit Boat Form Modal */}
          <MobileModal
            isOpen={showAddForm}
            onClose={resetBoatForm}
            title={editingBoat ? "Edit Equipment" : "Add New Equipment"}
            maxWidth="max-w-md"
          >
            <form onSubmit={editingBoat ? handleUpdateBoat : handleAddBoat}>
              <div className="mb-4">
                <label className="block text-sm font-semibold mb-2">Boat Name</label>
                <input
                  type="text"
                  value={boatName}
                  onChange={(e) => setBoatName(e.target.value)}
                  className="border p-3 rounded w-full text-base"
                  required
                />
              </div>
              <div className="mb-4">
                <label className="block text-sm font-semibold mb-2">Boat Class</label>
                <select
                  value={boatClass}
                  onChange={(e) => setBoatClass(e.target.value)}
                  className="border p-3 rounded w-full text-base"
                  required
                >
                  <option value="">Select a class...</option>
                  {ALL_EQUIPMENT_CLASSES.map(c => (
                    <option key={c.value} value={c.value}>{c.label}</option>
                  ))}
                </select>
              </div>
              <div className="mb-4">
                <label className="block text-sm font-semibold mb-2">Weight Limit (optional)</label>
                <input
                  type="text"
                  value={weightClass}
                  onChange={(e) => setWeightClass(e.target.value)}
                  className="border p-3 rounded w-full text-base"
                  placeholder="e.g., Under 160 lbs, 145-165 lbs, Heavyweight"
                />
              </div>
              <div className="mb-4">
                <label className="block text-sm font-semibold mb-2">Span/Spread (cm, optional)</label>
                <input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  value={boatSpanCm}
                  onChange={(e) => setBoatSpanCm(e.target.value.replace(/[^0-9]/g, ''))}
                  className="border p-3 rounded w-full text-base"
                  placeholder="e.g., 160"
                />
              </div>
              <div className="mb-4">
                <label className="block text-sm font-semibold mb-2">Ownership Type</label>
                <select
                  value={ownershipType}
                  onChange={(e) => setOwnershipType(e.target.value)}
                  className="border p-3 rounded w-full text-base"
                >
                  <option value="club">Club (anyone can reserve)</option>
                  <option value="private">Private (owner only)</option>
                </select>
              </div>
              {ownershipType === 'private' && (
                <div className="mb-4">
                  <label className="block text-sm font-semibold mb-2">
                    Owners {ownerIds.length > 0 && <span className="text-theme-primary">({ownerIds.length} selected)</span>}
                  </label>
                  <p className="text-sm text-gray-500 mb-2">Select all users who co-own this equipment</p>
                  <div className="border rounded-lg max-h-48 overflow-y-auto p-2">
                    {teamMembers.map((member) => (
                      <label
                        key={member.id}
                        className={`flex items-center gap-3 p-2 rounded cursor-pointer hover:bg-gray-50 ${
                          ownerIds.includes(member.id.toString()) ? 'bg-theme-bg-secondary' : ''
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={ownerIds.includes(member.id.toString())}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setOwnerIds([...ownerIds, member.id.toString()]);
                            } else {
                              setOwnerIds(ownerIds.filter(id => id !== member.id.toString()));
                            }
                          }}
                          className="w-4 h-4 rounded"
                        />
                        <div>
                          <div className="font-medium">{member.name}</div>
                          <div className="text-xs text-gray-500">{member.email}</div>
                        </div>
                      </label>
                    ))}
                  </div>
                  {ownerIds.length === 0 && (
                    <p className="text-red-500 text-sm mt-1">Please select at least one owner</p>
                  )}
                </div>
              )}
              <div className="flex flex-col sm:flex-row gap-2 mt-6">
                <button
                  type="submit"
                  className="btn-theme-primary px-4 py-3 rounded-lg flex-1 text-base"
                >
                  {editingBoat ? 'Save Changes' : 'Add Equipment'}
                </button>
                <button
                  type="button"
                  onClick={resetBoatForm}
                  className="bg-gray-500 text-white px-4 py-3 rounded-lg hover:bg-gray-600 flex-1 text-base"
                >
                  Cancel
                </button>
              </div>
            </form>
          </MobileModal>

      {/* Boat Rigging Modal */}
      <MobileModal
        isOpen={showBoatRiggingForm && editingBoatRigging}
        onClose={() => {
          setShowBoatRiggingForm(false);
          setEditingBoatRigging(null);
        }}
        title={editingBoatRigging ? `${editingBoatRigging.name} - Rigging` : 'Edit Rigging'}
        maxWidth="max-w-md"
      >
        {editingBoatRigging && (
          <form onSubmit={saveBoatRigging} className="space-y-4">
            <div>
              <label className="block text-sm font-semibold mb-2">Span/Spread (cm, optional)</label>
              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                value={boatSpanCm}
                onChange={(e) => setBoatSpanCm(e.target.value.replace(/[^0-9]/g, ''))}
                className="border p-3 rounded w-full text-base"
                placeholder="e.g., 160"
              />
            </div>
            <div className="flex gap-2">
              <button type="submit" className="btn-theme-primary px-4 py-2 rounded">Save Rigging</button>
              <button
                type="button"
                onClick={() => {
                  setShowBoatRiggingForm(false);
                  setEditingBoatRigging(null);
                }}
                className="bg-gray-300 text-gray-700 px-4 py-2 rounded hover:bg-gray-400"
              >
                Cancel
              </button>
            </div>
          </form>
        )}
      </MobileModal>

      {/* Meters Log Modal */}
          <MobileModal
            isOpen={showMetersLog && selectedBoat}
            onClose={() => {
                  setShowMetersLog(false);
                  setSelectedBoat(null);
                  setMeters('');
                  setMetersNotes('');
                  setMetersAssignedUserIds([]);
                  setMeterLogReservationId(null);
                  setShowUserSelector(false);
                  setError('');
              handleCancelEdit();
            }}
            title={selectedBoat ? `${selectedBoat.name} - Meters Log` : 'Meters Log'}
            maxWidth="max-w-4xl"
              >
            {selectedBoat && (
              <>
                <div className="mb-4 p-3 sm:p-4 bg-theme-bg-secondary rounded">
                  <p className="text-base sm:text-lg font-semibold">
                Total Meters: {formatNumber(selectedBoat.total_meters)}
              </p>
            </div>

            {/* Log Meters Form */}
                <form
              onSubmit={handleLogMeters}
              className={`mb-6 p-3 sm:p-4 border rounded bg-gray-50 ${editingLog ? 'opacity-60 pointer-events-none' : ''}`}
            >
              <h4 className="font-semibold mb-1">Log New Meters</h4>
              {editingLog && (
                <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1.5 mb-2">
                  You are editing a history entry below. Save or cancel that edit before using this form.
                </p>
              )}
              <p className="text-xs text-gray-500 mb-3">
                If you reserved this boat for this day and set a crew, they are credited when you log
                (or open the list below to change it). Leave &quot;Credit to athletes&quot; empty to
                credit only yourself.
              </p>
              {metersSuccess && (
                <div className="mb-3 bg-green-100 border border-green-300 text-green-700 px-3 py-2 rounded text-sm">
                  {metersSuccess}
                </div>
              )}
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4 mb-4">
                <div>
                  <label className="block text-sm font-semibold mb-2">Meters</label>
                  <input
                    type="number"
                    value={meters}
                    onChange={(e) => setMeters(e.target.value)}
                        className="border p-3 rounded w-full text-base"
                    placeholder="e.g., 5000"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold mb-2">Date</label>
                  <input
                        type="date"
                        value={metersDate.split('/').length === 3 ? `${metersDate.split('/')[2]}-${metersDate.split('/')[0].padStart(2,'0')}-${metersDate.split('/')[1].padStart(2,'0')}` : ''}
                        onChange={(e) => {
                          const [y, m, d] = e.target.value.split('-');
                          setMetersDate(`${m}/${d}/${y}`);
                        }}
                        className="border p-3 rounded w-full text-base"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold mb-2">Notes (optional)</label>
                  <input
                    type="text"
                    value={metersNotes}
                    onChange={(e) => setMetersNotes(e.target.value)}
                        className="border p-3 rounded w-full text-base"
                    placeholder="e.g., Morning practice"
                  />
                </div>
              </div>
              <div className="mb-4">
                <label className="block text-sm font-semibold mb-2">
                  Credit to athletes (optional)
                  {metersAssignedUserIds.length > 0 && (
                    <span className="text-blue-600 ml-2">({metersAssignedUserIds.length} selected)</span>
                  )}
                  {selectedBoat && (
                    <span className="text-gray-500 text-sm font-normal ml-1">
                      — max {getSeatCountForBoatClass(selectedBoat.boat_class)} ({selectedBoat.boat_class})
                    </span>
                  )}
                </label>
                <button
                  type="button"
                  onClick={() => setShowUserSelector(!showUserSelector)}
                  className="border p-3 rounded w-full text-base bg-white text-gray-900 text-left flex justify-between items-center"
                >
                  <span>
                    {metersAssignedUserIds.length === 0 
                      ? '— Select athletes to credit —'
                      : `${metersAssignedUserIds.length} athlete${metersAssignedUserIds.length > 1 ? 's' : ''} selected`
                    }
                  </span>
                  <svg className={`w-4 h-4 transform ${showUserSelector ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                {showUserSelector && (
                  <div className="border rounded mt-1 max-h-48 overflow-y-auto bg-white">
                    <div className="p-2 border-b bg-gray-50 flex justify-between items-center sticky top-0">
                      <button
                        type="button"
                        onClick={() => {
                          const cap = selectedBoat
                            ? getSeatCountForBoatClass(selectedBoat.boat_class)
                            : teamMembers.length;
                          setMetersAssignedUserIds(
                            teamMembers.slice(0, cap).map((m) => String(m.id))
                          );
                        }}
                        className="text-xs text-blue-600 hover:underline"
                      >
                        Select all
                      </button>
                      <button
                        type="button"
                        onClick={() => setMetersAssignedUserIds([])}
                        className="text-xs text-red-600 hover:underline"
                      >
                        Clear all
                      </button>
                    </div>
                    {teamMembers.map((m) => {
                      const mCap = selectedBoat
                        ? getSeatCountForBoatClass(selectedBoat.boat_class)
                        : 8;
                      return (
                      <label 
                        key={m.id} 
                        className="flex items-center p-2 hover:bg-gray-50 cursor-pointer border-b last:border-b-0"
                      >
                        <input
                          type="checkbox"
                          checked={metersAssignedUserIds.includes(String(m.id))}
                          onChange={(e) => {
                            if (e.target.checked) {
                              if (metersAssignedUserIds.length >= mCap) return;
                              setMetersAssignedUserIds([...metersAssignedUserIds, String(m.id)]);
                            } else {
                              setMetersAssignedUserIds(metersAssignedUserIds.filter(id => id !== String(m.id)));
                            }
                          }}
                          className="mr-3 h-4 w-4"
                        />
                        <span>{m.name}</span>
                        {m.team && <span className="text-xs text-gray-500 ml-2">({m.team})</span>}
                      </label>
                      );
                    })}
                  </div>
                )}
                <p className="text-xs text-gray-500 mt-1">
                  Select multiple athletes to credit this distance (e.g., all rowers in an 8+). Each selected athlete will have these meters added to their personal totals.
                </p>
              </div>
              <div className="flex flex-col sm:flex-row gap-2">
                <button
                  type="submit"
                  className="btn-theme-primary px-4 py-3 rounded-lg w-full sm:w-auto text-base"
                >
                  Log Meters
                </button>
                <button
                  type="button"
                  onClick={() => handleLogMeters(null, true)}
                  className="bg-green-600 text-white px-4 py-3 rounded-lg hover:bg-green-700 w-full sm:w-auto text-base"
                >
                  Save & Add Another
                </button>
              </div>
            </form>

            {/* Meters Log History */}
            <div>
              <h4 className="font-semibold mb-3">Meters History</h4>
              {metersLog.length === 0 ? (
                <p className="text-gray-500">No meters logged yet</p>
              ) : (
                <div className="space-y-2">
                  {metersLog.map((log) => (
                        <div key={log.id} className="border p-3 rounded bg-white">
                          {editingLog?.id === log.id ? (
                            // Edit form
                            <form onSubmit={handleUpdateMetersLog} className="space-y-3">
                              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                                <div>
                                  <label className="block text-xs font-semibold mb-1">Meters</label>
                                  <input
                                    type="number"
                                    value={editMeters}
                                    onChange={(e) => setEditMeters(e.target.value)}
                                    className="border p-3 rounded w-full text-base"
                                    required
                                  />
                                </div>
                                <div>
                                  <label className="block text-xs font-semibold mb-1">Date</label>
                                  <input
                                    type="date"
                                    value={editDate.split('/').length === 3 ? `${editDate.split('/')[2]}-${editDate.split('/')[0].padStart(2,'0')}-${editDate.split('/')[1].padStart(2,'0')}` : editDate}
                                    onChange={(e) => {
                                      const [y, m, d] = e.target.value.split('-');
                                      setEditDate(`${m}/${d}/${y}`);
                                    }}
                                    className="border p-3 rounded w-full text-base"
                                    required
                                  />
                                </div>
                                <div>
                                  <label className="block text-xs font-semibold mb-1">Notes</label>
                                  <input
                                    type="text"
                                    value={editNotes}
                                    onChange={(e) => setEditNotes(e.target.value)}
                                    className="border p-3 rounded w-full text-base"
                                  />
                                </div>
                                <div className="sm:col-span-3">
                                  <label className="block text-xs font-semibold mb-1">Credited to</label>
                                  <select
                                    value={editAssignedUserId}
                                    onChange={(e) => setEditAssignedUserId(e.target.value)}
                                    className="border p-3 rounded w-full text-base bg-white text-gray-900"
                                  >
                                    <option value="">— Unassigned —</option>
                                    {teamMembers.map((m) => (
                                      <option key={m.id} value={m.id}>
                                        {m.name}
                                      </option>
                                    ))}
                                  </select>
                                </div>
                              </div>
                              <div className="flex gap-2">
                                <button
                                  type="submit"
                                  className="btn-theme-primary px-4 py-2 rounded text-base flex-1 sm:flex-initial"
                                >
                                  Save
                                </button>
                                <button
                                  type="button"
                                  onClick={handleCancelEdit}
                                  className="bg-gray-300 text-gray-700 px-4 py-2 rounded text-base hover:bg-gray-400 flex-1 sm:flex-initial"
                                >
                                  Cancel
                                </button>
                              </div>
                            </form>
                          ) : (
                            // Display view - card style on mobile
                            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
                      <div>
                                <p className="font-semibold text-lg">{formatNumber(log.meters)} meters</p>
                        <p className="text-sm text-gray-600">
                                  {log.date} {log.notes && `• ${log.notes}`}
                        </p>
                        {(log.skip_boat_total === true || log.skipBoatTotal === true) && (
                          <p className="text-xs text-amber-700 mt-0.5">Not added to hull total</p>
                        )}
                        <p className="text-xs text-gray-500">
                          Logged by: {log.logged_by_name || 'Unknown'}
                        </p>
                        {(log.credited_to_names || log.assigned_to_name) && (
                          <p className="text-xs text-theme-primary font-medium mt-1">
                            Credited to: {log.credited_to_names || log.assigned_to_name}
                          </p>
                        )}
                      </div>
                              {canEditLog(log) && (
                                <div className="flex gap-2 items-center">
                                  <button
                                    onClick={() => handleStartEdit(log)}
                                    className="text-blue-600 hover:text-blue-800 text-sm px-3 py-2 rounded border border-blue-300 hover:bg-blue-50 flex-1 sm:flex-initial"
                                  >
                                    Edit
                                  </button>
                        <button
                          onClick={() => handleDeleteMetersLog(log.id)}
                                    className="text-red-600 hover:text-red-800 px-3 py-2 rounded border border-red-300 hover:bg-red-50 flex-1 sm:flex-initial"
                                  >
                                    Delete
                                  </button>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </>
            )}
          </MobileModal>

          {/* Reservations Modal */}
          <MobileModal
            isOpen={showReservations && selectedBoat}
            onClose={() => {
              setShowReservations(false);
              setSelectedBoat(null);
              setError('');
            }}
            title={selectedBoat ? `${selectedBoat.name} - Reservations` : 'Reservations'}
            maxWidth="max-w-4xl"
          >
            {selectedBoat && (
              <>
                {/* Boat Info */}
                <div className="flex items-center gap-2 mb-4 flex-wrap">
                  {getOwnershipBadge(selectedBoat)}
                  <span className="text-sm text-gray-600">{selectedBoat.boat_class}</span>
                </div>

                {/* Reservation Form */}
                {canReserveBoat(selectedBoat) ? (
                  <form onSubmit={handleCreateReservation} className="mb-6 p-3 sm:p-4 border rounded bg-gray-50">
                    <h4 className="font-semibold mb-3">Make a Reservation</h4>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 mb-4">
                      <div>
                        <label className="block text-sm font-semibold mb-2">Date</label>
                        <input
                          type="date"
                          value={reservationDate}
                          onChange={(e) => {
                            setReservationDate(e.target.value);
                            setJustReservedId(null);
                          }}
                          min={getLocalISODate()}
                          className="border p-3 rounded w-full text-base"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold mb-2">Purpose (optional)</label>
                        <input
                          type="text"
                          value={purpose}
                          onChange={(e) => setPurpose(e.target.value)}
                          className="border p-3 rounded w-full text-base"
                          placeholder="e.g., Race training"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold mb-2">Start Time</label>
                        <select
                          value={startTime}
                          onChange={(e) => {
                            setStartTime(e.target.value);
                            setJustReservedId(null);
                          }}
                          className="border p-3 rounded w-full text-base"
                          required
                        >
                          {RESERVATION_TIME_OPTIONS.map((t) => (
                            <option key={t} value={t}>
                              {formatReservationTimeLabel(t)}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-semibold mb-2">End Time</label>
                        <select
                          value={endTime}
                          onChange={(e) => {
                            setEndTime(e.target.value);
                            setJustReservedId(null);
                          }}
                          className="border p-3 rounded w-full text-base"
                          required
                        >
                          {RESERVATION_TIME_OPTIONS.map((t) => (
                            <option key={t} value={t}>
                              {formatReservationTimeLabel(t)}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="sm:col-span-2">
                        {reservationHoldsOnDate.length === 0 ? (
                          <p className="text-sm text-green-700 bg-green-50 border border-green-200 rounded p-2">
                            No other reservations on this boat that day.
                          </p>
                        ) : (
                          <div
                            className={`text-sm rounded p-2 border ${
                              reservationConflicts.length > 0
                                ? 'bg-red-50 border-red-300 text-red-800'
                                : justBookedHold
                                  ? 'bg-green-50 border-green-200 text-green-900'
                                  : 'bg-amber-50 border-amber-200 text-amber-900'
                            }`}
                          >
                            <p className="font-semibold mb-1">
                              {reservationConflicts.length > 0
                                ? 'Your time overlaps a reservation. Pick a free time:'
                                : justBookedHold
                                  ? 'Booked. Pick another time to reserve again. Reserved that day:'
                                  : 'Already reserved that day:'}
                            </p>
                            <ul className="space-y-0.5">
                              {reservationHoldsOnDate.map((r) => {
                                const clashes = reservationConflicts.some((c) => c.id === r.id);
                                const justBooked = r.id === justReservedId;
                                return (
                                  <li key={r.id} className={clashes || justBooked ? 'font-semibold' : ''}>
                                    {formatTime(r.start_time)} – {formatTime(r.end_time)}
                                    {r.user_name ? ` · ${r.user_name}` : ''}
                                    {clashes ? ' (overlaps)' : justBooked ? ' (just booked)' : ''}
                                  </li>
                                );
                              })}
                            </ul>
                          </div>
                        )}
                      </div>
                      {!isLaunchEquipment(selectedBoat.boat_class) && (
                      <div className="sm:col-span-2">
                        <p className="text-sm font-semibold mb-1">Crew (optional, meter auto-credit)</p>
                        <p className="text-xs text-gray-500 mb-2">
                          If set, when you log meters for this boat and date (and you don&apos;t pick
                          different people in the form), the same distance is credited to these club
                          members in their meters. You can select at most{' '}
                          {getSeatCountForBoatClass(selectedBoat.boat_class)} (this boat&apos;s seat count).
                        </p>
                        <SearchableMemberChecklist
                          members={teamMembers}
                          selectedIds={reservationAssignedUserIds}
                          onChange={setReservationAssignedUserIds}
                          maxSelected={getSeatCountForBoatClass(selectedBoat.boat_class)}
                        />
                      </div>
                      )}
                    </div>
                    <button
                      type="submit"
                      disabled={submittingReservation || reservationOverlaps.length > 0}
                      className="btn-theme-primary px-4 py-3 rounded-lg w-full sm:w-auto text-base disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {submittingReservation ? 'Reserving…' : 'Reserve Boat'}
                    </button>
                  </form>
                ) : (
                  <div className="mb-6 p-3 sm:p-4 border rounded bg-yellow-50 text-yellow-800">
                    <p className="font-semibold">This is a private boat</p>
                    <p className="text-sm">Only the owner{selectedBoat.owners?.length > 1 ? 's' : ''} ({selectedBoat.owner_name}) can make reservations for this boat.</p>
                  </div>
                )}

                {/* Existing Reservations */}
                <div>
                  <h4 className="font-semibold mb-3">Upcoming Reservations</h4>
                  {reservations.length === 0 ? (
                    <p className="text-gray-500">No reservations for this boat</p>
                  ) : (
                    <div className="space-y-2">
                      {reservations.map((res) => (
                        <div key={res.id} className="border p-3 rounded bg-white flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
                          <div>
                            <p className="font-semibold text-base">{res.reservation_date}</p>
                            <p className="text-sm">
                              {formatTime(res.start_time)} - {formatTime(res.end_time)}
                            </p>
                            <p className="text-sm text-gray-600">
                              Reserved by: {res.user_name}
                              {res.purpose && ` • ${res.purpose}`}
                            </p>
                            {res.assignedUsers?.length > 0 && (
                              <p className="text-xs text-theme-primary mt-1">
                                <span className="font-medium">Meter auto-credit:</span>{' '}
                                {res.assignedUsers.map((u) => u.name).join(', ')}
                              </p>
                            )}
                          </div>
                          <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
                            {(res.user_id === user?.id || isCoach() || isAdmin() || isSuperAdmin()) &&
                              !isLaunchEquipment(selectedBoat.boat_class) && (
                              <button
                                type="button"
                                onClick={() => {
                                  setEditingCrewForReservation(res);
                                  setEditingCrewUserIds(
                                    (res.assignedUsers || []).map((u) => String(u.id))
                                  );
                                }}
                                className="text-theme-primary px-3 py-2 rounded border border-theme-primary/30 hover:bg-theme-primary/5 text-sm w-full sm:w-auto"
                              >
                                Edit crew
                              </button>
                            )}
                            {(res.user_id === user?.id || isCoach()) && (
                              <button
                                onClick={() => handleCancelReservation(res.id)}
                                className="text-red-600 hover:text-red-800 px-3 py-2 rounded border border-red-300 hover:bg-red-50 w-full sm:w-auto"
                              >
                                Cancel
                              </button>
                            )}
                          </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
              </>
      )}
          </MobileModal>

      <MobileModal
        isOpen={!!editingCrewForReservation}
        onClose={() => {
          setEditingCrewForReservation(null);
          setEditingCrewUserIds([]);
        }}
        title="Crew — auto-credit for meters"
        maxWidth="max-w-md"
      >
        <div className="p-4">
          <p className="text-sm text-gray-600 mb-3">
            When the reserver logs meters for this boat and date, these people are credited
            the same distance (unless a different list is set on the log). Coaches with access
            can set this for any member&apos;s reservation. You can select at most{' '}
            {getSeatCountForBoatClass(selectedBoat?.boat_class)} for this equipment.
          </p>
          <SearchableMemberChecklist
            members={teamMembers}
            selectedIds={editingCrewUserIds}
            onChange={setEditingCrewUserIds}
            maxSelected={getSeatCountForBoatClass(selectedBoat?.boat_class)}
            className="max-h-64 overflow-y-auto border rounded p-2 space-y-1 bg-white mb-4"
          />
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={saveReservationCrew}
              className="btn-theme-primary px-4 py-2 rounded text-sm"
            >
              Save
            </button>
            <button
              type="button"
              onClick={() => {
                setEditingCrewForReservation(null);
                setEditingCrewUserIds([]);
              }}
              className="px-4 py-2 border rounded text-sm"
            >
              Cancel
            </button>
          </div>
        </div>
      </MobileModal>

          {/* Damage Reports Modal */}
          <MobileModal
            isOpen={showDamageReports && selectedBoat}
            onClose={() => {
              setShowDamageReports(false);
              setSelectedBoat(null);
              setShowAddDamageReport(false);
              setDamageAssigneeUserId('');
              setResolvingReport(null);
              setError('');
            }}
            title={selectedBoat ? `${selectedBoat.name} - Damage Reports` : 'Damage Reports'}
            maxWidth="max-w-4xl"
          >
            {selectedBoat && (
              <>
                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 mb-4">
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-600">{selectedBoat.boat_class}</span>
                    {selectedBoat.ownership_type === 'private' && (
                      <span className="bg-purple-100 text-purple-700 text-xs px-2 py-0.5 rounded">
                        Private
                      </span>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <select
                      value={damageStatusFilter}
                      onChange={(e) => {
                        setDamageStatusFilter(e.target.value);
                        fetchBoatDamageReports(selectedBoat.id, e.target.value);
                      }}
                      className="border p-2 rounded text-sm"
                    >
                      <option value="all">All Reports</option>
                      <option value="open">Open</option>
                      <option value="in_progress">In Progress</option>
                      <option value="resolved">Resolved</option>
                    </select>
                    <button
                      onClick={() => setShowAddDamageReport(true)}
                      className="btn-theme-primary px-3 py-2 rounded text-sm flex items-center gap-1"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                      </svg>
                      Report Damage
                    </button>
                  </div>
                </div>

                {/* Add Damage Report Form */}
                {showAddDamageReport && (
                  <form onSubmit={handleSubmitDamageReport} className="mb-6 p-4 border rounded bg-gray-50">
                    <h4 className="font-semibold mb-3">Report New Damage</h4>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                      <div>
                        <label className="block text-sm font-semibold mb-2">Severity *</label>
                        <select
                          value={damageSeverity}
                          onChange={(e) => setDamageSeverity(e.target.value)}
                          className="border p-3 rounded w-full text-base"
                          required
                        >
                          <option value="minor">Minor - Cosmetic / No impact on use</option>
                          <option value="moderate">Moderate - Use with caution</option>
                          <option value="severe">Severe - Limited use only</option>
                          <option value="critical">Critical - DO NOT USE</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-semibold mb-2">Date Discovered *</label>
                        <input
                          type="date"
                          value={damageDate.split('/').length === 3 ? `${damageDate.split('/')[2]}-${damageDate.split('/')[0].padStart(2,'0')}-${damageDate.split('/')[1].padStart(2,'0')}` : ''}
                          onChange={(e) => {
                            const [y, m, d] = e.target.value.split('-');
                            setDamageDate(`${m}/${d}/${y}`);
                          }}
                          className="border p-3 rounded w-full text-base"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold mb-2">Location on Boat</label>
                        <input
                          type="text"
                          value={damageLocation}
                          onChange={(e) => setDamageLocation(e.target.value)}
                          className="border p-3 rounded w-full text-base"
                          placeholder="e.g., bow, rigger 3, hull port side"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold mb-2">Assign To (optional)</label>
                        <select
                          value={damageAssigneeUserId}
                          onChange={(e) => setDamageAssigneeUserId(e.target.value)}
                          className="border p-3 rounded w-full text-base"
                        >
                          <option value="">Unassigned</option>
                          {teamMembers.map(member => (
                            <option key={member.id} value={member.id}>{member.name}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                    <div className="mb-4">
                      <label className="block text-sm font-semibold mb-2">Description *</label>
                      <textarea
                        value={damageDescription}
                        onChange={(e) => setDamageDescription(e.target.value)}
                        className="border p-3 rounded w-full text-base min-h-[100px]"
                        placeholder="Describe the damage in detail..."
                        required
                      />
                    </div>
                    <div className="flex gap-2">
                      <button
                        type="submit"
                        className="btn-theme-primary px-4 py-2 rounded"
                      >
                        Submit Report
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setShowAddDamageReport(false);
                          setDamageSeverity('minor');
                          setDamageDescription('');
                          setDamageLocation('');
                          setDamageAssigneeUserId('');
                        }}
                        className="bg-gray-300 text-gray-700 px-4 py-2 rounded hover:bg-gray-400"
                      >
                        Cancel
                      </button>
                    </div>
                  </form>
                )}

                {/* Damage Reports List */}
                <div>
                  <h4 className="font-semibold mb-3">
                    {damageStatusFilter === 'all' ? 'All Reports' : `${damageStatusFilter.charAt(0).toUpperCase() + damageStatusFilter.slice(1).replace('_', ' ')} Reports`}
                    {' '}({damageReports.length})
                  </h4>
                  {damageReports.length === 0 ? (
                    <p className="text-gray-500 text-center py-8">
                      {damageStatusFilter === 'all' 
                        ? 'No damage reports for this boat. Great!' 
                        : `No ${damageStatusFilter.replace('_', ' ')} reports`}
                    </p>
                  ) : (
                    <div className="space-y-3">
                      {damageReports.map((report) => (
                        <div key={report.id} className="border rounded-lg p-4 bg-white">
                          <div className="flex flex-wrap items-start justify-between gap-2 mb-2">
                            <div className="flex flex-wrap gap-2">
                              <span className={`px-2 py-1 rounded text-xs font-semibold ${getSeverityColor(report.severity)}`}>
                                {report.severity.toUpperCase()}
                              </span>
                              <span className={`px-2 py-1 rounded text-xs font-semibold ${getStatusColor(report.status)}`}>
                                {report.status.replace('_', ' ').toUpperCase()}
                              </span>
                            </div>
                            <span className="text-xs text-gray-500">
                              {report.discovered_date}
                            </span>
                          </div>
                          
                          {report.location_on_boat && (
                            <p className="text-sm text-gray-600 mb-1">
                              <span className="font-semibold">Location:</span> {report.location_on_boat}
                            </p>
                          )}
                          
                          <p className="text-sm mb-2 whitespace-pre-wrap">{report.description}</p>
                          
                          <p className="text-xs text-gray-500 mb-2">
                            Reported by {report.reported_by_name} on {new Date(report.created_at).toLocaleDateString()}
                          </p>

                          <div className="flex items-center gap-2 mb-2">
                            <span className="text-xs text-gray-600 font-semibold">Assigned:</span>
                            <select
                              value={report.assignee_user_id || ''}
                              onChange={(e) => handleUpdateDamageReport(report.id, null, e.target.value)}
                              className="border p-1 rounded text-xs"
                            >
                              <option value="">Unassigned</option>
                              {teamMembers.map(member => (
                                <option key={member.id} value={member.id}>{member.name}</option>
                              ))}
                            </select>
                            {report.assignee_name && (
                              <span className="text-xs text-indigo-600">{report.assignee_name}</span>
                            )}
                          </div>

                          {report.resolved_by_name && (
                            <p className="text-xs text-green-600 mb-2">
                              Resolved by {report.resolved_by_name}
                              {report.resolution_notes && `: ${report.resolution_notes}`}
                            </p>
                          )}

                          {/* Resolve form */}
                          {resolvingReport === report.id ? (
                            <div className="mt-3 p-3 bg-gray-50 rounded border">
                              <label className="block text-sm font-semibold mb-2">Resolution Notes</label>
                              <textarea
                                value={resolutionNotes}
                                onChange={(e) => setResolutionNotes(e.target.value)}
                                className="border p-2 rounded w-full text-sm mb-2"
                                placeholder="Describe how the issue was resolved..."
                                rows={2}
                              />
                              <div className="flex flex-wrap gap-2">
                                <button
                                  onClick={() => handleUpdateDamageReport(report.id, 'resolved')}
                                  className="bg-green-600 text-white px-3 py-1 rounded text-sm hover:bg-green-700"
                                >
                                  Mark Resolved
                                </button>
                                <button
                                  onClick={() => {
                                    setResolvingReport(null);
                                    setResolutionNotes('');
                                  }}
                                  className="text-gray-600 px-3 py-1 rounded text-sm hover:bg-gray-200"
                                >
                                  Cancel
                                </button>
                              </div>
                            </div>
                          ) : (
                            <div className="flex flex-wrap gap-2 mt-2">
                              {(report.status === 'open' || report.status === 'in_progress') && (
                                <>
                                  {report.status === 'open' && (
                                    <button
                                      onClick={() => handleUpdateDamageReport(report.id, 'in_progress')}
                                      className="text-blue-600 text-sm hover:underline"
                                    >
                                      Mark In Progress
                                    </button>
                                  )}
                                  <button
                                    onClick={() => setResolvingReport(report.id)}
                                    className="text-green-600 text-sm hover:underline"
                                  >
                                    Resolve
                                  </button>
                                </>
                              )}
                              {(isCoach() || isAdmin()) && (
                                <button
                                  onClick={() => handleDeleteDamageReport(report.id)}
                                  className="text-red-600 text-sm hover:underline"
                                >
                                  Delete
                                </button>
                              )}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </>
            )}
          </MobileModal>

      {/* Equipment List */}
          {(() => {
            // Filter boats based on search query and erg filter
            const filteredBoats = boats.filter(boat => {
              const matchesSearch = boat.name.toLowerCase().includes(searchQuery.toLowerCase());
              const matchesErgFilter = showErgsOnly ? isErgEquipment(boat.boat_class) : true;
              const matchesLaunchFilter = showLaunchesOnly ? isLaunchEquipment(boat.boat_class) : true;
              return matchesSearch && matchesErgFilter && matchesLaunchFilter;
            });
            
            // Separate active and retired boats
            const activeBoats = filteredBoats.filter(boat => !boat.is_retired);
            const retiredBoats = filteredBoats.filter(boat => boat.is_retired);

            const renderBoatCard = (boat) => (
              <div key={boat.id} className={`border rounded-lg p-3 sm:p-4 shadow hover:shadow-lg transition-shadow ${boat.is_retired ? 'bg-gray-100 border-gray-300' : 'bg-white'}`}>
                <div className="flex justify-between items-start mb-2">
                  <div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className={`text-base sm:text-lg font-bold ${boat.is_retired ? 'text-gray-500' : 'text-theme-primary'}`}>{boat.name}</h3>
                      {boat.is_retired && (
                        <span className="bg-gray-500 text-white text-xs font-bold px-2 py-0.5 rounded">
                          RETIRED
                        </span>
                      )}
                      {damageReportCounts[boat.id] > 0 && (
                        <span className="bg-red-500 text-white text-xs font-bold px-1.5 py-0.5 rounded-full">
                          {damageReportCounts[boat.id]}
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-gray-600">
                      {boat.boat_class}
                      {boat.weight_class && (
                        <span className="ml-1 text-xs bg-gray-100 px-1.5 py-0.5 rounded">
                          {boat.weight_class}
                        </span>
                      )}
                    </p>
                    {boat.span_cm && (
                      <p className="text-xs text-gray-500 mt-1">
                        Rigging: Span/Spread {boat.span_cm}cm
                      </p>
                    )}
                    <div className="mt-1">{getOwnershipBadge(boat)}</div>
                    {boat.is_retired && boat.retired_at && (
                      <p className="text-xs text-gray-400 mt-1">
                        Retired {new Date(boat.retired_at).toLocaleDateString()}
                        {boat.retired_by_name && ` by ${boat.retired_by_name}`}
                      </p>
                    )}
                  </div>
                  {((isCoach() || isAdmin()) || canEditBoatRigging(boat)) && (
                    <div className="flex items-center gap-1">
                      {!boat.is_retired && (isCoach() || isAdmin()) && (
                        <>
                          <button
                            onClick={() => handleEditBoat(boat)}
                            className="text-blue-600 hover:text-blue-800 p-1"
                            title="Edit"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                          </button>
                          <button
                            onClick={() => setShowRetireModal(boat)}
                            className="text-amber-600 hover:text-amber-800 p-1"
                            title="Retire"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
                            </svg>
                          </button>
                          <button
                            onClick={() => handleDeleteBoat(boat.id)}
                            className="text-red-600 hover:text-red-800 font-bold text-xl p-1"
                            title="Delete"
                          >
                            ×
                          </button>
                        </>
                      )}
                      {!boat.is_retired && canEditBoatRigging(boat) && (
                        <button
                          onClick={() => openBoatRiggingForm(boat)}
                          className="text-indigo-600 hover:text-indigo-800 text-xs px-2 py-1 border border-indigo-300 rounded hover:bg-indigo-50"
                          title="Edit Rigging"
                        >
                          Rigging
                        </button>
                      )}
                      {boat.is_retired && (isCoach() || isAdmin()) && (
                        <button
                          onClick={() => handleUnretireBoat(boat.id)}
                          className="text-green-600 hover:text-green-800 text-xs px-2 py-1 border border-green-300 rounded hover:bg-green-50"
                        >
                          Reactivate
                        </button>
                      )}
                    </div>
                  )}
                </div>
                
                {/* Only show meters for shells (not ergs or launches) */}
                {tracksMeters(boat.boat_class) && !boat.is_retired && (
                  <div className="mb-3">
                    <p className="text-xs sm:text-sm font-semibold text-gray-700">Total Meters:</p>
                    <p className="text-xl sm:text-2xl font-bold text-theme-primary">{formatNumber(boat.total_meters)}</p>
                  </div>
                )}
                
                {/* Action buttons - hide most for retired boats */}
                {!boat.is_retired && (
                  <div className="flex gap-2">
                    {tracksMeters(boat.boat_class) && (
                      <button
                        onClick={() => handleViewMetersLog(boat)}
                        className="flex-1 btn-theme-secondary px-2 sm:px-3 py-2 rounded text-xs sm:text-sm"
                      >
                        Meters
                      </button>
                    )}
                    {isReservableClass(boat.boat_class) && (
                      <button
                        onClick={() => handleViewReservations(boat)}
                        className="flex-1 btn-theme-primary px-2 sm:px-3 py-2 rounded text-xs sm:text-sm"
                      >
                        Reserve
                      </button>
                    )}
                    {/* Damage reports available for all equipment */}
                    <button
                      onClick={() => handleViewDamageReports(boat)}
                      className={`${isErgEquipment(boat.boat_class) ? 'flex-1' : ''} px-2 sm:px-3 py-2 rounded text-xs sm:text-sm flex items-center justify-center ${
                        damageReportCounts[boat.id] > 0 
                          ? 'bg-red-100 text-red-700 hover:bg-red-200' 
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                      title="Damage Reports"
                    >
                      {isErgEquipment(boat.boat_class) ? (
                        <span className="flex items-center gap-1">
                          <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          </svg>
                          Damage Reports
                        </span>
                      ) : (
                        <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                      )}
                    </button>
                  </div>
                )}
                
                {/* View history button for retired boats */}
                {boat.is_retired && tracksMeters(boat.boat_class) && (isCoach() || isAdmin()) && (
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleViewMetersLog(boat)}
                      className="flex-1 btn-theme-secondary px-2 sm:px-3 py-2 rounded text-xs sm:text-sm opacity-75"
                    >
                      View History
                    </button>
                  </div>
                )}
              </div>
            );

            return (
              <div className="space-y-6">
                {/* Active Equipment */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
                  {activeBoats.length === 0 && retiredBoats.length === 0 ? (
                    <p className="text-gray-500 col-span-full text-sm sm:text-base">
                      {searchQuery || showErgsOnly || showLaunchesOnly ? `No equipment found matching your filters` : 'No equipment added yet. Click "Add Equipment" to get started.'}
                    </p>
                  ) : activeBoats.length === 0 ? (
                    <p className="text-gray-500 col-span-full text-sm sm:text-base">
                      No active equipment found. {showRetired && retiredBoats.length > 0 ? 'See retired equipment below.' : ''}
                    </p>
                  ) : (
                    activeBoats.map(renderBoatCard)
                  )}
                </div>

                {/* Retired Equipment Section - Only for coaches/admins when showing retired */}
                {showRetired && retiredBoats.length > 0 && (isCoach() || isAdmin()) && (
                  <div className="mt-8">
                    <div className="border-t pt-6">
                      <h3 className="text-lg font-semibold text-gray-500 mb-4 flex items-center gap-2">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
                        </svg>
                        Retired Equipment ({retiredBoats.length})
                      </h3>
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
                        {retiredBoats.map(renderBoatCard)}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })()}
        </>
      )}
    </div>
  );
}
